/**
 * Block registry.
 *
 * Blocks are plain numeric IDs stored in flat typed arrays - never objects.
 * This is the single most important performance decision in a voxel game:
 * Minecraft's Java edition allocated block objects and paid for it in GC
 * pauses for a decade. A `Uint16Array` gives us 65k block types, cache-local
 * access, and zero allocation during world traversal.
 */

export const enum BlockId {
  Air = 0,
  Grass = 1,
  Dirt = 2,
  Stone = 3,
  /** Чорнозем - the black earth. Ukraine's defining soil. */
  Chornozem = 4,
  Sand = 5,
  Clay = 6,
  /** Солома - dried grass, the roof of every хата. */
  Thatch = 7,
  /** Цегла - fired clay. */
  Brick = 8,
  /** Мазанка - clay and straw daub, whitewashed. The signature wall. */
  Mazanka = 9,
  Sandstone = 10,
  /**
   * Незламний блок: the floor of the world, and the wall across it.
   *
   * **Numbered above `FIRST_NON_BLOCK_ITEM`, like the lying logs**, because
   * there is no such thing as one of these in a bag: it drops nothing, it is
   * placed by the map and never by a player, and `palette.ts` skips
   * everything up here when it lists what can be held. The low numbers ran
   * out long ago in any case - 191 was the last one, and адамантій needed it
   * because адамантій *is* carried.
   *
   * Nothing takes it out - not a pick, not a вибух, not the режим творця.
   * It exists so a map can have edges that are not arguments: the bottom of
   * a world nobody can dig through, and, on «Сучасна війна», the wall
   * between two armies for as long as the preparing lasts.
   */
  Bedrock = 320,
  /**
   * Шопопало-блок: the whole of the map «Один блок».
   *
   * "Гравці зявляються на одному блоці а навколо порожнеча... Після 4 ударів
   * по блоку з нього може випасти будь-який з існуючих блоків чи речей чи
   * істот." So it is not a block you find, mine and carry off: it is the
   * world's one source, it never comes away, and four blows shake something
   * loose out of it.
   *
   * Numbered up here with the незламний блок for the same reason: nobody can
   * ever hold one. There is no item form, it is on no shelf, and no recipe
   * makes it - it is put down by the map and stays where it was put.
   */
  Random = 321,
  /**
   * Адамантій: the blue-white crystal at the bottom of the world.
   *
   * "Його можна копати тільки залізним кайлом, він на самому дні світів +10
   * блоків." The one thing a wooden pick cannot touch, and the only material
   * that breaks a прапор - so a team that wants to win has to go down for
   * it first.
   */
  Adamant = 191,
  /** Бруківка - the cobbled paving of an old town square. */
  Cobble = 11,
  /** Кам'яна плитка - dressed and laid in courses. */
  StoneTile = 12,
  /** Дуб - the heavy oak of the forest edge. */
  OakLog = 13,
  OakLeaves = 14,
  /** Біла акація - lighter, airier, all over the Ukrainian countryside. */
  AcaciaLog = 15,
  AcaciaLeaves = 16,
  /** Дубові дошки. Id unchanged from when there was only one kind. */
  OakPlanks = 17,
  /** Вода. Not solid, not opaque, and drawn in a blended pass. */
  Water = 18,
  /** Дика пшениця - grows wild on the steppe, drops seeds. */
  WildWheat = 19,
  /** Рілля - soil broken by the hoe, dry. */
  Farmland = 20,
  /** Рілля, волога - within reach of water, and where crops will grow. */
  FarmlandWet = 21,
  /** Посіяна пшениця: паростки. */
  WheatCrop0 = 22,
  /** Зелена пшениця. */
  WheatCrop1 = 23,
  /** Стигла пшениця - ready to harvest. */
  WheatCrop2 = 24,
  AcaciaPlanks = 25,
  /** Скло. Solid underfoot, but you see straight through it. */
  Glass = 26,
  /** Вікно в дубовій рамі. */
  OakWindow = 27,
  /** Вікно в рамі з акації. */
  AcaciaWindow = 28,
  /** Глиняний чан, порожній. */
  Vat = 29,
  /** Чан із водою - тут вимочують льон. */
  VatFull = 30,
  /** Дикий льон - росте на степу, дає насіння. */
  WildFlax = 31,
  FlaxCrop0 = 32,
  FlaxCrop1 = 33,
  /** Цвіте синім - видно здалеку. */
  FlaxCrop2 = 34,
  /** Прядка. */
  SpinningWheel = 35,
  /**
   * Дикі коноплі.
   *
   * Content for grown-ups only: generated when, and only when, the 18+ switch
   * is on. Nothing is made from it yet - it grows, and that is all.
   */
  WildHemp = 36,

  /**
   * Дикий буряк, on the steppe.
   *
   * Where беt comes from before anybody has sown any: the garden seed has to
   * be findable, or the whole crop is something you can only ever steal out
   * of somebody else's город. 190 is one of the two numbers left below the
   * item range, which is where a block that is also a thing you can hold has
   * to live.
   */
  WildBeet = 190,

  /*
   * What is under the ground.
   *
   * The list is Ukraine's own: Donbas coal, Prykarpattia oil, Polissia peat,
   * Kryvyi Rih iron, Nikopol manganese, the Irshansk titanium sands, Zavallia
   * graphite and Zhytomyr granite. Where each one is generated follows where
   * it actually occurs, as far as a 96-block world can - titanium in sand
   * rather than in rock is the clearest case, and the one most people would
   * get wrong.
   */
  /** Кам'яне вугілля. */
  CoalOre = 37,
  /** Залізна руда. */
  IronOre = 38,
  /** Марганцева руда. */
  ManganeseOre = 39,
  /** Графіт. */
  GraphiteOre = 40,
  /** Нафтоносний пласт - просочена нафтою порода. */
  OilShale = 41,
  /** Граніт. Не руда, а камінь: із нього будують. */
  Granite = 42,
  /** Торф. Копається лопатою, як і належить. */
  Peat = 43,
  /** Ільменіт - титанистий пісок. */
  Ilmenite = 44,
  /** Рутил - другий титановий пісок, рідший. */
  Rutile = 45,

  /**
   * Оркське лігво.
   *
   * Where the raids come from, and the only thing in the game that has to be
   * destroyed rather than merely built. Generation puts them on the far
   * island; breaking the last one ends the raids for good.
   */
  OrcSpawner = 50,

  /**
   * Паростки. What falls out of a crown when you cut it, and what puts a
   * forest back after you have felled one.
   *
   * Two of them, because there are two kinds of tree and a дуб that grew out
   * of an acacia sprout would be a lie the player can see.
   */
  OakSapling = 51,
  AcaciaSapling = 52,

  /**
   * Сосна, and everything that comes off it.
   *
   * The third tree, and the first one grown for something other than its
   * wood: смола runs out of a pine trunk and out of nothing else, and a
   * смолоскип needs it.
   */
  PineLog = 53,
  PineLeaves = 54,
  PinePlanks = 55,
  PineSapling = 56,

  /**
   * Смолоскип: a stick, a rag of лляне полотно and pine смола.
   *
   * Drawn at full brightness rather than tinted with the hour, so it is worth
   * carrying after dark. It does not yet light what is around it - that needs
   * a light pass through the chunks, which this game does not have.
   */
  Torch = 57,
  /** Креслярський камінь: put it down and a хата stands up behind it. */
  Blueprint = 58,

  /**
   * Білий камінь - dressed limestone, the білокам'яна architecture.
   *
   * The stone every old cathedral in this part of the world is faced with,
   * and the one thing this game had no answer for: мазанка is daub, and a
   * cathedral of daub is a lie you can see from across the map.
   */
  WhiteStone = 62,
  /** Зелена черепиця - glazed roof tile, the green of every palace roof. */
  GreenTile = 63,

  /**
   * Позолочена бляха - gilded sheet, what a dome is actually covered in.
   *
   * Sheet rather than solid gold, so it needs no ore and tells no lie about
   * where it came from: a dome is thin metal over a wooden shell.
   */
  Gilt = 112,
  /**
   * Рубінова зірка. The thing on top of the spire.
   *
   * It glows, which makes it the second light source in the game after the
   * смолоскип, and the first one you can see from the far island.
   */
  RubyStar = 113,
  /** Креслярський камінь: кремль. Its four facings run 114-117. */
  KremlinStone = 114,

  /**
   * Унітаз. An ordinary modern lavatory, in a game about a хата.
   *
   * It falls out of orcs, rarely. There is no explanation and it does not
   * want one - it is the joke, and a joke that gets explained stops being
   * one. Placeable like any other block.
   */
  Toilet = 118,

  /**
   * Вибухоблок. The whole of «Король гори», and nothing anywhere else.
   *
   * Three in ten go off when broken, and there is no way to tell which from
   * the outside - they are deliberately identical, which is the entire game.
   * Which ones are live is decided by where they are rather than by a die at
   * break time; the reasoning is in core/world/kingHill.ts and it is the
   * difference between one map and two players in different ones.
   *
   * Earth, by material, so it comes away in the hand. A hill you needed a
   * pickaxe to climb would be a hill nobody could start climbing.
   */
  Boom = 119,

  /**
   * Сніг. What the cold biomes wear, and what a mountain wears above a line.
   *
   * Its own block rather than a flag on grass: it lies on stone as readily as
   * on soil, it is what you see from a distance, and "grass but white" would
   * have to be true of every soil there is.
   *
   * Numbered above the требушет because 128 is the shutters' - sixteen ids
   * in a packed range, which is exactly the sort of thing a free-looking
   * number turns out to be.
   */
  /**
   * Проклятий камінь, and what is built out of it.
   *
   * The замок Чахлика is cut into a mountain rather than raised on one, so
   * its stone has to read as *cut*: dark slate with cold blue in it, beside
   * the ordinary grey the rest of the world is made of. The lamp is the same
   * blue as the fire he throws - one colour for the thing and its house.
   */
  CursedStone = 172,
  CursedBrick = 173,
  CursedLamp = 174,
  /** Мазанка: the whitewashed clay wall every хата in the country has. */
  Whitewash = 175,
  Snow = 148,
  /**
   * Лід. Water that is not water, in the cold.
   *
   * Solid and walkable - a frozen lake you can cross is most of why a winter
   * looks like a winter from the shore.
   */
  Ice = 149,
}

/**
 * Требушет.
 *
 * Four ids, one per facing, because it throws the way it is pointed and a
 * siege engine that fires wherever you happen to be looking is not a siege
 * engine. Its own range, like the doors and the stoves, for the same reason:
 * what a placed block *means* is which way it is turned.
 */
/**
 * Сходи - stairs, in wood and in stone.
 *
 * Four ids each, one per facing, the same as a door or a требушет: what a
 * placed stair *means* is which way it climbs, and a staircase that faced
 * wherever the block happened to be put would be a pile.
 *
 * **They collide as whole blocks.** There is no sub-cell physics in this
 * game - `isSolid` answers for a cell and nothing finer - so the air under
 * the overhang is solid, exactly as it is inside a унітаз or a требушет.
 * That makes a staircase built of them behave like a staircase built of
 * blocks, which is what it is for; it does not let you stand in the notch.
 */
export const WOOD_STAIRS_BASE = 120;
export const STONE_STAIRS_BASE = 124;
export const STAIRS_COUNT = 4;

export function woodStairsId(facing: DoorFacing): number {
  return WOOD_STAIRS_BASE + facing;
}

export function stoneStairsId(facing: DoorFacing): number {
  return STONE_STAIRS_BASE + facing;
}

/**
 * Сходи turned over, for hanging under a floor.
 *
 * "Роби щоб сходи можливо було ставити перевернуто якщо ставляться до блока
 * знизу вверх." Their own range rather than a bit inside the existing one:
 * wood sits at 120 and stone at 124, four ids apart, so there is no room to
 * widen either without moving the other - and moving a block id rewrites
 * every saved world that has one in it.
 *
 * Eight: four facings of wood, then four of stone, in the same order.
 */
export const FLIPPED_STAIRS_BASE = 182;
export const FLIPPED_STAIRS_COUNT = 8;

export function flippedStairsId(stone: boolean, facing: DoorFacing): number {
  return FLIPPED_STAIRS_BASE + (stone ? 4 : 0) + facing;
}

/**
 * Which stair to put down, given the face that was clicked.
 *
 * "Якщо ставляться до блока знизу вверх" - the underside of a block has a
 * normal of -1 in y and nothing else does, so the face settles it. Here
 * rather than in the placement code because it is a rule and not a gesture:
 * the branch in main.ts cannot be argued with in a test, and this can.
 */
export function stairsForFace(
  stone: boolean, facing: DoorFacing, ny: number,
): number {
  if (ny === -1) return flippedStairsId(stone, facing);
  return stone ? stoneStairsId(facing) : woodStairsId(facing);
}

/** Is this the upside-down kind: a slab at the top with the step under it? */
export function stairsFlipped(id: number): boolean {
  return id >= FLIPPED_STAIRS_BASE &&
    id < FLIPPED_STAIRS_BASE + FLIPPED_STAIRS_COUNT;
}

export function isStairs(id: number): boolean {
  return (id >= WOOD_STAIRS_BASE && id < WOOD_STAIRS_BASE + STAIRS_COUNT) ||
    (id >= STONE_STAIRS_BASE && id < STONE_STAIRS_BASE + STAIRS_COUNT) ||
    stairsFlipped(id);
}

/** Which way it climbs: the high side is toward this facing. */
export function stairsFacing(id: number): DoorFacing {
  // The flipped range is its own base and has to be taken off it, or an
  // upside-down stair reads its facing off the stone base and comes out
  // pointing somewhere else entirely.
  if (stairsFlipped(id)) return ((id - FLIPPED_STAIRS_BASE) & 3) as DoorFacing;
  return ((id - (id >= STONE_STAIRS_BASE ? STONE_STAIRS_BASE : WOOD_STAIRS_BASE))
    & 3) as DoorFacing;
}

export const TREBUCHET_BASE = 144;
export const TREBUCHET_COUNT = 4;

export function trebuchetId(facing: DoorFacing): number {
  return TREBUCHET_BASE + facing;
}

export function isTrebuchet(id: number): boolean {
  return id >= TREBUCHET_BASE && id < TREBUCHET_BASE + TREBUCHET_COUNT;
}

export function trebuchetFacing(id: number): DoorFacing {
  return ((id - TREBUCHET_BASE) & 3) as DoorFacing;
}

/** How brightly the зірка burns. Dimmer than a смолоскип - it is a lamp. */
export const STAR_LIGHT = 10;

/**
 * Креслярський камінь.
 *
 * A building in a block: put it down and a whole хата stands up behind it.
 * Four ids, one per facing, because the building has a front and it has to
 * know which way the door goes.
 *
 * Its own range rather than four loose ids, for the same reason the doors and
 * the stoves have one: what a placed block *means* is its facing, and packing
 * that into the id is how every other turnable thing in this game works.
 */
export const BLUEPRINT_BASE = 58;
export const BLUEPRINT_COUNT = 4;

/**
 * The кремль stone, and why it is not simply BLUEPRINT_BASE + 4.
 *
 * The хата stone occupies 58-61 and the doors start at 64, which leaves
 * exactly two ids in between - not enough for a second building's four
 * facings. The obvious tidy answer, widening the blueprint range and shifting
 * the doors up, is the one thing that must not happen: a block id is what a
 * saved chunk stores, and moving one turns every door in somebody's world
 * into something else. That has already cost this player his world twice.
 *
 * So a second range, in free space, and a table that ties the two together.
 */
export const KREMLIN_BASE = 114;
export const KREMLIN_COUNT = 4;

/**
 * Every креслярський камінь there is.
 *
 * The building is named rather than referenced, because the buildings live in
 * core/world/blueprint.ts and that file imports this one - naming it keeps
 * the arrow pointing one way.
 */
export const PLAN_STONES: readonly {
  readonly base: number;
  readonly count: number;
  readonly building: string;
  readonly label: string;
  readonly labelEn: string;
  /** [side, top] tiles. */
  readonly tiles: readonly [number, number];
}[] = [
  {
    base: BLUEPRINT_BASE, count: BLUEPRINT_COUNT, building: "hata",
    label: "креслярський камінь", labelEn: "drafting stone", tiles: [122, 123],
  },
  {
    base: KREMLIN_BASE, count: KREMLIN_COUNT, building: "kremlin",
    label: "креслярський камінь: кремль", labelEn: "drafting stone: кремль",
    tiles: [128, 129],
  },
];

export function blueprintId(facing: DoorFacing): number {
  return BLUEPRINT_BASE + facing;
}

export function kremlinId(facing: DoorFacing): number {
  return KREMLIN_BASE + facing;
}

/** Any креслярський камінь, of any building. */
export function isBlueprint(id: number): boolean {
  return PLAN_STONES.some((s) => id >= s.base && id < s.base + s.count);
}

/**
 * The first id of the range this stone belongs to, or -1.
 *
 * What "one entry per thing" means for the палітра: a stone has four facings
 * and the shelf wants one of them.
 */
export function blueprintBase(id: number): number {
  const stone = PLAN_STONES.find((s) => id >= s.base && id < s.base + s.count);
  return stone === undefined ? -1 : stone.base;
}

/** Which building this stone carries, or undefined if it is not a stone. */
export function blueprintBuilding(id: number): string | undefined {
  return PLAN_STONES.find((s) => id >= s.base && id < s.base + s.count)?.building;
}

export function blueprintFacing(id: number): DoorFacing {
  const base = blueprintBase(id);
  return ((base < 0 ? 0 : id - base) & 3) as DoorFacing;
}

/**
 * Doors.
 *
 * A door needs four independent things: which wood, which half, which way it
 * faces, and whether it is open. That is 2 x 2 x 4 x 2 = 32 combinations, and
 * rather than invent a block-state system for one block, they occupy a
 * contiguous range of ids with the state packed into the offset.
 *
 * Block ids are Uint16, so ranges are cheap. If a second block ever needs
 * state this becomes the wrong answer and a proper state table becomes the
 * right one - but paying for that machinery now, for doors alone, would be
 * paying early.
 */
export const DOOR_BASE = 64;
export const DOOR_COUNT = 32;

export type DoorWood = 0 | 1;
/** 0 north (-Z), 1 east (+X), 2 south (+Z), 3 west (-X). */
export type DoorFacing = 0 | 1 | 2 | 3;

export interface DoorState {
  wood: DoorWood;
  facing: DoorFacing;
  upper: boolean;
  open: boolean;
}

export function doorId(
  wood: DoorWood, facing: DoorFacing, upper: boolean, open: boolean,
): number {
  return DOOR_BASE + ((wood * 4 + facing) * 2 + (upper ? 1 : 0)) * 2 + (open ? 1 : 0);
}

export function isDoor(id: number): boolean {
  return id >= DOOR_BASE && id < DOOR_BASE + DOOR_COUNT;
}

export function doorState(id: number): DoorState {
  const n = id - DOOR_BASE;
  return {
    open: (n & 1) === 1,
    upper: ((n >> 1) & 1) === 1,
    facing: ((n >> 2) & 3) as DoorFacing,
    wood: ((n >> 4) & 1) as DoorWood,
  };
}

/**
 * Which edge of its cell the panel sits against.
 *
 * Closed, it fills the doorway; open, it has swung a quarter turn clockwise
 * and lies against the next wall along.
 */
/**
 * Печі.
 *
 * Two finishes - bare clay and limed white - each turned one of four ways and
 * either cold or alight, so sixteen ids in a range of their own. Same trick as
 * the doors, for the same reason: it is one block, and inventing a block-state
 * table for it would be inventing a system to avoid writing sixteen lines.
 *
 * **Why the fire is in the block id and not just in the Furnace.** What a
 * stove is burning lives beside the grid, in world/furnace.ts, along with what
 * is in its slots - and none of that reaches the mesher, which bakes a tile
 * per face at meshing time from the block id alone. A stove that looked
 * different while alight without the id changing would need the mesher to
 * know about furnaces, which is a much bigger hole in a much better wall.
 */
export const STOVE_BASE = 96;
export const STOVE_COUNT = 16;

/** 0 bare clay, 1 limed white. */
export type StoveFinish = 0 | 1;

export function stoveId(
  finish: StoveFinish, facing: DoorFacing, lit = false,
): number {
  return STOVE_BASE + (lit ? 8 : 0) + finish * 4 + facing;
}

export function isStove(id: number): boolean {
  return id >= STOVE_BASE && id < STOVE_BASE + STOVE_COUNT;
}

export function stoveState(
  id: number,
): { finish: StoveFinish; facing: DoorFacing; lit: boolean } {
  const n = id - STOVE_BASE;
  return {
    finish: ((n >> 2) & 1) as StoveFinish,
    facing: (n & 3) as DoorFacing,
    lit: (n & 8) !== 0,
  };
}

/** Is there a fire in this one right now? */
export function stoveLit(id: number): boolean {
  return isStove(id) && (id - STOVE_BASE & 8) !== 0;
}

/**
 * The same stove, lit or put out.
 *
 * Turned rather than replaced, so nothing else about it changes: a stove that
 * catches and then goes out must come back facing the same way, in the same
 * finish, still holding whatever was in its slots.
 */
export function withStoveLit(id: number, lit: boolean): number {
  if (!isStove(id)) return id;
  const { finish, facing } = stoveState(id);
  return stoveId(finish, facing, lit);
}

/**
 * Віконниці.
 *
 * A shutter is a door one block tall: same thin panel, same swing, no second
 * half. Two woods turned four ways, open or shut - sixteen ids in their own
 * range, next to the doors and the stoves.
 */
/**
 * Тин: a post with rails between the posts.
 *
 * One id, because a fence has no facing of its own - it joins to whatever is
 * beside it, which is what `wallConnects` already works out for the мазанка
 * wall. The same shape, in wood and thinner, which is why it costs a
 * definition rather than a renderer.
 */
export const FENCE = 150;

/**
 * Лава, deep down where the stone is warm.
 *
 * Not a fluid that runs: it sits in the pools the cave-carving leaves it in,
 * and stepping into one is the mistake it is meant to be. Making it flow is
 * the water simulation again with a second set of rules and a second set of
 * bugs, and it would not add a single thing anybody wants from lava, which
 * is light at the bottom of a shaft and a reason to look where you are
 * going.
 */
export const LAVA = 151;

/**
 * Хвіртка: the way through a тин.
 *
 * Eight ids, four facings by open and shut, the same shape as a шутер and
 * for the same reason: a gate is a panel that swings, and this game already
 * knows how to draw one and how to toggle it.
 */
export const GATE_BASE = 152;
export const GATE_COUNT = 8;

export function gateId(facing: DoorFacing, open: boolean): number {
  return GATE_BASE + facing * 2 + (open ? 1 : 0);
}

export function isGate(id: number): boolean {
  return id >= GATE_BASE && id < GATE_BASE + GATE_COUNT;
}

export function gateState(id: number): { facing: DoorFacing; open: boolean } {
  const n = id - GATE_BASE;
  return { open: (n & 1) === 1, facing: ((n >> 1) & 3) as DoorFacing };
}

export const SHUTTER_BASE = 128;
export const SHUTTER_COUNT = 16;

export function shutterId(
  wood: DoorWood, facing: DoorFacing, open: boolean,
): number {
  return SHUTTER_BASE + (wood * 4 + facing) * 2 + (open ? 1 : 0);
}

export function isShutter(id: number): boolean {
  return id >= SHUTTER_BASE && id < SHUTTER_BASE + SHUTTER_COUNT;
}

export function shutterState(
  id: number,
): { wood: DoorWood; facing: DoorFacing; open: boolean } {
  const n = id - SHUTTER_BASE;
  return {
    open: (n & 1) === 1,
    facing: ((n >> 1) & 3) as DoorFacing,
    wood: ((n >> 3) & 1) as DoorWood,
  };
}

/**
 * Рушник.
 *
 * A hung cloth: the same thin panel as a shutter, but it never opens - so
 * four ids, one per wall it can face, and no state beyond that.
 */
/**
 * Смолоскип на стіні.
 *
 * Four ids, one per wall it hangs on, the same as a рушник or a скриня.
 * Standing on the floor it is the plain `BlockId.Torch` and the cross every
 * plant uses; against a wall it needs geometry of its own, because a torch
 * that hangs in the middle of the cell beside the wall is a torch floating in
 * the air - "смолоскипи не мають текстури ставлення на стіну", and that is
 * what it looked like.
 *
 * The facing is which wall it is *on*: 0 north, and the torch leans out of
 * that wall into the room.
 */
export const WALL_TORCH_BASE = 168;
export const WALL_TORCH_COUNT = 4;

export function wallTorchId(facing: DoorFacing): number {
  return WALL_TORCH_BASE + facing;
}

export function isWallTorch(id: number): boolean {
  return id >= WALL_TORCH_BASE && id < WALL_TORCH_BASE + WALL_TORCH_COUNT;
}

export function wallTorchFacing(id: number): DoorFacing {
  return ((id - WALL_TORCH_BASE) & 3) as DoorFacing;
}

/** A смолоскип of either kind: the one on the floor and the four on walls. */
export function isTorch(id: number): boolean {
  return id === BlockId.Torch || isWallTorch(id);
}

export const RUSHNYK_BASE = 192;
export const RUSHNYK_COUNT = 4;

export function rushnykId(facing: DoorFacing): number {
  return RUSHNYK_BASE + facing;
}

export function isRushnyk(id: number): boolean {
  return id >= RUSHNYK_BASE && id < RUSHNYK_BASE + RUSHNYK_COUNT;
}

/**
 * Flowing water.
 *
 * A source (BlockId.Water) is infinite and never moves. Everything it spreads
 * is one of these: eight ids, one per level, level 1 next to the source and
 * level WATER_RANGE at the far end of the reach. That is what "water flows
 * eight blocks" means - the level *is* the distance travelled.
 *
 * The range sits at 256 rather than in the gap under 200, because item ids
 * begin at 200 and block items keep the block's own id. A block that will
 * never be an item can live above the item range; a block that shares numbers
 * with an unrelated item cannot, and saves are written as bare numbers.
 */
export const WATER_BASE = 256;
/** How far a source reaches, and so how many flowing levels there are. */
export const WATER_RANGE = 8;

/** The flowing block `level` steps from a source. Level 0 is the source. */
export function waterFlowId(level: number): number {
  return WATER_BASE + Math.max(1, Math.min(WATER_RANGE, level)) - 1;
}

export function isWaterFlow(id: number): boolean {
  return id >= WATER_BASE && id < WATER_BASE + WATER_RANGE;
}

/** An infinite source: what generation lays down and what a bucket pours. */
export function isWaterSource(id: number): boolean {
  return id === BlockId.Water;
}

/** Source or flow. */
export function isWater(id: number): boolean {
  return id === BlockId.Water || isWaterFlow(id);
}

/**
 * How high water of this level stands in its cell, 0..1.
 *
 * A source fills the block. Every step away from it stands lower, so a stream
 * is visibly a slope and you can see which way it runs without being told.
 * The last level is a film on the ground rather than a puddle, which is what
 * makes the end of a flow read as an end rather than as a wall.
 *
 * Falling water is the exception and it is not expressible here: a column
 * dropping through the air fills its whole cell whatever its level. That
 * depends on the block above, so the mesher applies it - see fluidBounds.
 */
export function waterHeight(id: number): number {
  const level = waterLevel(id);
  if (level <= 0) return 1;
  return (WATER_RANGE + 1 - level) / (WATER_RANGE + 1);
}

/** Steps from the nearest source: 0 for a source, 1..WATER_RANGE for a flow. */
export function waterLevel(id: number): number {
  if (id === BlockId.Water) return 0;
  return isWaterFlow(id) ? id - WATER_BASE + 1 : -1;
}

/**
 * Flowing лава, on the same scheme as flowing water and for the same reasons.
 *
 * Its own range: four blocks against water's eight. Лава that ran as far as
 * water would fill a cave system from one pool, and the pools this map
 * generates are the floors of the deepest caves - the whole of the bottom of
 * the world would be лава within a minute of anybody digging into one.
 *
 * At 512, and not at 264 straight after the water.
 *
 * Water sits at 256 because item ids "begin at 200" - which was true and is
 * no longer the whole story: the items have since grown past 256 and the
 * newest of them, a човен, is 280. Putting лава immediately after the water
 * put four blocks on top of four spawn eggs, and a save is bare numbers -
 * a block that shares a number with an item is a block that comes back as a
 * свиня in somebody's bag. The check caught it in the same breath it was
 * written.
 *
 * So: well clear, with room for the items to go on growing. Anything else
 * that needs a run of block ids should be up here too.
 */
/**
 * Нафта: the third fluid, and the one you go down for.
 *
 * "Додай нафту десь ближче до дна. Поведінка нафти як у води, тільки тече
 * повільніше на 30%." So: a liquid you can swim in and drown in, standing in
 * pockets down where the лава is, and slower over the ground than water -
 * which is what makes a pool of it something you watch spread rather than
 * something that has already spread.
 *
 * Numbered up here with the flows rather than among the blocks: every low id
 * was gone long ago, and a cell of нафта is not something anybody carries.
 * What you carry is a pailful, which is `PlainItemId.CrudeOil` and already
 * existed for the shale.
 */
export const OIL = 330;
export const OIL_BASE = 336;
export const OIL_RANGE = 4;

/** The flowing нафта `level` steps from a pool. Level 0 is the pool itself. */
export function oilFlowId(level: number): number {
  return OIL_BASE + Math.max(1, Math.min(OIL_RANGE, level)) - 1;
}

export function isOilFlow(id: number): boolean {
  return id >= OIL_BASE && id < OIL_BASE + OIL_RANGE;
}

export function isOil(id: number): boolean {
  return id === OIL || isOilFlow(id);
}

/** Steps from the nearest pool: 0 for a pool, 1..OIL_RANGE for a flow. */
export function oilLevel(id: number): number {
  if (id === OIL) return 0;
  return isOilFlow(id) ? id - OIL_BASE + 1 : -1;
}

/** How high нафта of this level stands in its cell, 0..1. See waterHeight. */
export function oilHeight(id: number): number {
  const level = oilLevel(id);
  if (level <= 0) return 1;
  return (OIL_RANGE + 1 - level) / (OIL_RANGE + 1);
}

export const LAVA_BASE = 512;
export const LAVA_RANGE = 4;

/** The flowing лава `level` steps from a pool. Level 0 is the pool itself. */
export function lavaFlowId(level: number): number {
  return LAVA_BASE + Math.max(1, Math.min(LAVA_RANGE, level)) - 1;
}

export function isLavaFlow(id: number): boolean {
  return id >= LAVA_BASE && id < LAVA_BASE + LAVA_RANGE;
}

/** Steps from the nearest pool: 0 for a pool, 1..LAVA_RANGE for a flow. */
export function lavaLevel(id: number): number {
  if (id === LAVA) return 0;
  return isLavaFlow(id) ? id - LAVA_BASE + 1 : -1;
}

/** How high лава of this level stands in its cell, 0..1. See waterHeight. */
export function lavaHeight(id: number): number {
  const level = lavaLevel(id);
  if (level <= 0) return 1;
  return (LAVA_RANGE + 1 - level) / (LAVA_RANGE + 1);
}

/**
 * Мазана стіна.
 *
 * A post half a block across with arms reaching to whatever it touches, and it
 * works out which arms to grow by itself. The first version was a flat panel
 * turned by whoever placed it, which is not what a wall is: you would build a
 * corner and get two panels passing each other rather than meeting.
 *
 * The state is **not** in the id. Which arms exist depends on the four
 * neighbours, and the mesher can see those, so it decides at mesh time - which
 * means placing a wall next to one changes both without anything having to go
 * round and update them.
 *
 * Four ids all the same, and only 46 is ever placed. The other three were
 * facings before this was rewritten, and any world saved in between has them
 * in it; they stay walls so those worlds still open.
 */
export const WALL_BASE = 46;
export const WALL_COUNT = 4;

/**
 * The granite wall, in the next free run of ids there is.
 *
 * 176 to 179, because 46 to 49 is the мазанка wall and its three legacy
 * facings and there is nothing between them and 176 that is not spoken for.
 * One id is placed and the other three exist for the same reason the
 * мазанка's do - if a facing is ever wanted, a saved world that predates it
 * still opens.
 */
export const GRANITE_WALL_BASE = 176;
export const GRANITE_WALL_COUNT = 4;

export function graniteWallId(): number {
  return GRANITE_WALL_BASE;
}

/**
 * Плити: half a block, lying on the floor of its cell.
 *
 * "З мазанки створи також блоки стіни та плити в горизонтальні в пів блока."
 * One id each and no facing - a slab lies down, and a slab on the ceiling is
 * a thing this game has no use for yet.
 *
 * They are only possible at all because a cell is a height now rather than a
 * yes: before that a плита would have been drawn at half a block and walked
 * on at a whole one, which is what сходи still do.
 */
export const MAZANKA_SLAB = 180;
export const GRANITE_SLAB = 181;

export function isSlab(id: number): boolean {
  return id === MAZANKA_SLAB || id === GRANITE_SLAB;
}
/** How thick the wall is. Half a block, which is the point of it. */
export const WALL_POST = 0.5;
/**
 * How wide an arm is, and how tall it stands: exactly the post.
 *
 * A stone garden wall has arms narrower and lower than its posts, which leaves
 * a notch at the side and a slot above - and that is right for a fence you are
 * meant to see through. A мазанка wall is plastered daub: a partition, and
 * since the raids it is also what you hide behind. Holes in it are holes.
 *
 * They stay separate constants because the shape is still a post with arms -
 * that is what turns corners and finishes ends correctly - and because saying
 * "the arm is the post" is worth saying out loud.
 */
export const WALL_ARM = WALL_POST;
export const WALL_ARM_HEIGHT = 1;

export function wallId(): number {
  return WALL_BASE;
}

export function isWall(id: number): boolean {
  return id >= WALL_BASE && id < WALL_BASE + WALL_COUNT;
}

/**
 * Does a wall grow an arm toward this?
 *
 * Another wall, or anything that fills its cell. Not doors, beds or plants:
 * an arm reaching into a doorway would stick through it, and a wall that
 * grabbed at every stalk of wheat beside it would never look straight.
 */
export function wallConnects(id: number): boolean {
  if (isWall(id)) return true;
  // A тин joins to a тин and to the хвіртка in it - a gate with a gap at
  // each shoulder is a gate standing in a field rather than in a fence.
  if (id === FENCE || isGate(id)) return true;
  return id !== BlockId.Air && isSolid(id) && !isShapeRender(id);
}

/**
 * Скриня.
 *
 * Four ids, one per way it can face. What it *holds* is not in the id - it
 * cannot be, twenty-seven stacks do not pack into a number - and lives in
 * world/containers.ts keyed by position.
 *
 * The range sits in the last four numbers below where items begin. A chest is
 * something you carry, so its item has to keep the block's own id space, and
 * 196 to 199 is what was left.
 */
export const CHEST_BASE = 196;
export const CHEST_COUNT = 4;

export function chestId(facing: DoorFacing): number {
  return CHEST_BASE + facing;
}

export function isChest(id: number): boolean {
  return id >= CHEST_BASE && id < CHEST_BASE + CHEST_COUNT;
}

/**
 * Ліжко.
 *
 * Two blocks like a door, but lying down: a head and a foot, turned four ways.
 * Eight ids in their own range.
 */
export const BED_BASE = 160;
export const BED_COUNT = 8;

export function bedId(head: boolean, facing: DoorFacing): number {
  return BED_BASE + (head ? 4 : 0) + facing;
}

export function isBed(id: number): boolean {
  return id >= BED_BASE && id < BED_BASE + BED_COUNT;
}

export function bedState(id: number): { head: boolean; facing: DoorFacing } {
  const n = id - BED_BASE;
  return { head: n >= 4, facing: (n & 3) as DoorFacing };
}

/** The step from the foot of a bed toward its head, per facing. */
export const FACING_STEP: readonly (readonly [number, number])[] = [
  [0, -1], // 0 north, -Z
  [1, 0],  // 1 east, +X
  [0, 1],  // 2 south, +Z
  [-1, 0], // 3 west, -X
];

/**
 * Which edge of its cell a hinged panel sits against.
 *
 * Doors and shutters swing the same way, so the mesher asks this rather than
 * asking what kind of thing it is drawing.
 */
export function panelEdge(id: number): DoorFacing {
  // A rushnyk hangs flat and never swings, so it is simply its facing.
  if (isRushnyk(id)) return (id - RUSHNYK_BASE) as DoorFacing;
  if (isWallTorch(id)) return wallTorchFacing(id);
  /*
   * A хвіртка, which had no branch here and needed one.
   *
   * Gates live at 152 and pack their state as `facing * 2 + open`; doors
   * live at 64 and pack theirs as `open | upper<<1 | facing<<2`. With no
   * branch a gate fell through to `doorState`, which read a gate id with
   * the door base and the door's bit layout - and the four facings came out
   * as two edges, 2, 2, 3, 3. Half of them put the panel against the same
   * wall of the cell whichever way the player was looking, which is exactly
   * how it was reported: "хвіртка ставиться завжди на одну і ту саму
   * сторону блоку".
   *
   * The placement code was never wrong. It asked for `gateId(facing)` and
   * got it; the id was right all the way into the mesher, and the mesher
   * asked this function where to put the panel.
   */
  if (isGate(id)) {
    const { facing, open } = gateState(id);
    return (open ? (facing + 1) % 4 : facing) as DoorFacing;
  }
  const { facing, open } = isShutter(id) ? shutterState(id) : doorState(id);
  return (open ? (facing + 1) % 4 : facing) as DoorFacing;
}

/** @deprecated Use panelEdge - kept so door call sites read naturally. */
export function doorPanelEdge(id: number): DoorFacing {
  return panelEdge(id);
}

/** Texture tile indices into the atlas, per face. */
export interface BlockFaces {
  top: number;
  bottom: number;
  side: number;
  /**
   * The face the block turns toward the player, when it has one.
   *
   * Used with `facing`: a stove's firebox belongs on one wall, not on four.
   */
  front?: number;
}

export interface BlockDef {
  readonly id: BlockId;
  /**
   * Internal name, for recipes, tests and the палітра.
   *
   * *Not* what a save stores - chunks store the numeric id, and only the id.
   * Renaming a block is therefore safe and renumbering one is not, which is
   * the exact opposite of what this comment used to claim.
   */
  readonly name: string;
  /** Ukrainian display name. The original, and the fallback for nothing. */
  readonly label: string;
  /**
   * The same name in English. Required.
   *
   * A block added without one does not compile. That is the point: the
   * alternative is an English game showing "чорнозем" in the hotbar, which
   * nobody would notice until a player did.
   */
  readonly en: string;
  /** Does the player collide with it? */
  readonly solid: boolean;
  /**
   * Does it hide the face of the block behind it?
   *
   * Separate from `solid` because leaves are both: you stand on them, but you
   * can see through the gaps, so the block behind must still be drawn. Rolling
   * the two together is why leaves look like a solid green cube in a naive
   * voxel engine.
   */
  readonly opaque: boolean;
  /**
   * Drawn in the blended pass rather than the opaque one.
   *
   * Separate from `opaque` again: leaves are see-through but are drawn with an
   * alpha test in the opaque pass, which needs no sorting. Only genuinely
   * see-*into* blocks like water pay for blending.
   */
  readonly translucent: boolean;
  /**
   * How the block is built as geometry.
   *
   * "cross" is two quads standing on their diagonals, the shape every block
   * game uses for grass and flowers. A plant drawn as a cube reads as a
   * textured box, which is unmistakably wrong.
   */
  readonly render:
    "cube" | "cross" | "door" | "bed" | "fluid" | "wall" | "stairs" | "torch"
    | "slab" | "trebuchet"
    /** Nothing from the mesher: the scene stands a model in the cell. */
    | "model";
  /**
   * Which way the block is turned, for blocks with a distinct front.
   * 0 north (-Z), 1 east (+X), 2 south (+Z), 3 west (-X).
   */
  readonly facing?: DoorFacing;
  /**
   * What breaking it yields. Undefined means it drops itself, which is true
   * of nearly everything; an empty array means it drops nothing.
   */
  readonly drops?: readonly ItemDrop[];
  readonly faces: BlockFaces;
  /**
   * What it looks like on the belt and in the bag, when its top face would
   * lie about it.
   *
   * A stair's faces are its material's, and a square of stone says "a block
   * of stone". This is the picture of the *shape* - see the stairIcon
   * tiles - and only the few blocks whose shape is the point have one.
   */
  readonly icon?: number;
}

/** One possible drop from breaking a block. */
export interface ItemDrop {
  /** An item id - see items.ts. For block items this equals the block id. */
  item: number;
  min: number;
  max: number;
  /** Probability the drop happens at all, 0-1. Defaults to certain. */
  chance?: number;
}

interface DefOptions {
  solid?: boolean;
  opaque?: boolean;
  translucent?: boolean;
  render?:
    "cube" | "cross" | "door" | "bed" | "fluid" | "wall" | "stairs" | "torch"
    | "slab" | "trebuchet"
    /** Nothing from the mesher: the scene stands a model in the cell. */
    | "model";
  facing?: DoorFacing;
  drops?: readonly ItemDrop[];
}

function def(
  id: BlockId,
  name: string,
  label: string,
  /** The English name, beside the Ukrainian so the pair is visible at a glance. */
  en: string,
  faces: BlockFaces,
  opts: DefOptions = {},
): BlockDef {
  const solid = opts.solid ?? true;
  return {
    id, name, label, en, solid,
    opaque: opts.opaque ?? solid,
    translucent: opts.translucent ?? false,
    render: opts.render ?? "cube",
    ...(opts.facing !== undefined ? { facing: opts.facing } : {}),
    ...(opts.drops !== undefined ? { drops: opts.drops } : {}),
    faces,
  };
}

/**
 * Every block, listed in id order for readability.
 *
 * Look blocks up with `blockDef`, never by indexing this directly: the two
 * agreed only by accident until a block was inserted out of order, and three
 * blocks quietly started returning each other's definitions.
 */
/**
 * Прапори: one per team, and the чорний прапор the bots fly.
 *
 * Five ids from 312: teams 0 to 3, then the pirate. Up here with the lying
 * logs and the beet, past `FIRST_NON_BLOCK_ITEM`, because a прапор is never
 * in anybody's bag - it is placed by the match and taken out of the world
 * by an адамантієве кайло, and nothing else happens to it.
 *
 * Whose flag it is has to be in the id rather than in a side table: the
 * server tells a page "this cell is now air" and nothing else, and a page
 * that could not tell whose flag had just come down could not say who had
 * won.
 */
export const FLAG_BASE = 312;
export const FLAG_COUNT = 5;

/** The чорний прапор, which is the bots' and nobody else's. */
export const PIRATE_FLAG = FLAG_BASE + 4;

export function flagId(team: number): number {
  return FLAG_BASE + Math.max(0, Math.min(FLAG_COUNT - 1, team));
}

export function isFlag(id: number): boolean {
  return id >= FLAG_BASE && id < FLAG_BASE + FLAG_COUNT;
}

/** Whose flag this is, or -1 for anything that is not one. */
export function flagTeam(id: number): number {
  return isFlag(id) ? id - FLAG_BASE : -1;
}

export const BLOCKS: readonly BlockDef[] = [
  def(BlockId.Air, "air", "повітря", "air", { top: 0, bottom: 0, side: 0 },
    { solid: false }),
  // Green on top, dark earth on every other face.
  /*
   * Cut it and you have earth in your hands, not turf.
   *
   * A block of grass is earth with something growing on the top of it, and
   * the something does not survive being dug up - which is true of every
   * spade of turf anybody has ever lifted, and is what makes the green come
   * back rather than be carried about in a bag. See `spreadGrass`.
   */
  // «Земля з травою», not «трава»: the plant that grows on the steppe is
  // also трава, and two things with one name on the shelf is one too many.
  def(BlockId.Grass, "grass", "земля з травою", "dirt with grass", { top: 1, bottom: 2, side: 3 },
    { drops: [{ item: BlockId.Dirt, min: 1, max: 1 }] }),
  def(BlockId.Dirt, "dirt", "земля", "earth", { top: 2, bottom: 2, side: 2 }),
  def(BlockId.Stone, "stone", "камінь", "stone", { top: 4, bottom: 4, side: 4 }),
  def(BlockId.Chornozem, "chornozem", "чорнозем", "black earth", { top: 5, bottom: 5, side: 5 }),
  def(BlockId.Sand, "sand", "пісок", "sand", { top: 6, bottom: 6, side: 6 }),
  def(BlockId.Clay, "clay", "глина", "clay", { top: 7, bottom: 7, side: 7 }),
  def(BlockId.Thatch, "thatch", "солома", "thatch", { top: 8, bottom: 8, side: 8 }),
  def(BlockId.Brick, "brick", "цегла", "brick", { top: 9, bottom: 9, side: 9 }),
  def(BlockId.Mazanka, "mazanka", "мазанка", "daub", { top: 10, bottom: 10, side: 10 }),
  def(BlockId.Sandstone, "sandstone", "пісковик", "sandstone", { top: 11, bottom: 11, side: 11 }),
  def(BlockId.Cobble, "cobble", "бруківка", "cobbles", { top: 12, bottom: 12, side: 12 }),
  def(BlockId.StoneTile, "stone_tile", "кам'яна плитка", "stone tile", { top: 13, bottom: 13, side: 13 }),
  def(BlockId.OakLog, "oak_log", "дубова колода", "oak log", { top: 15, bottom: 15, side: 14 }),
  // Leaves are solid underfoot but see-through, so they must not cull.
  // Leaves drop nothing yet - saplings arrive with tree planting.
  def(BlockId.OakLeaves, "oak_leaves", "дубове листя", "oak leaves", { top: 16, bottom: 16, side: 16 },
    {
      opaque: false,
      // A crown is about thirty blocks, so one in twelve is two or three
      // паростки per felled oak: enough to put the tree back and plant one
      // more, not enough to make a forest out of an afternoon.
      drops: [{ item: 51 /* OakSapling */, min: 1, max: 1, chance: 0.085 }],
    }),
  def(BlockId.AcaciaLog, "acacia_log", "акацієва колода", "acacia log", { top: 18, bottom: 18, side: 17 }),
  // Сосна: red-brown plated bark, and resin in the trunk.
  def(BlockId.PineLog, "pine_log", "соснова колода", "pine log", { top: 114, bottom: 114, side: 113 },
    {
      /*
       * The log, and the смола that runs out of it - every time.
       *
       * It was one in three, on the reasoning that a felled pine should be a
       * torch or two rather than a supply. In play that is not what it felt
       * like: смола is what a смолоскип is made of, you need one before dark
       * on the first day, and two trees out of three giving nothing reads as
       * the tree being broken rather than as the resin being scarce.
       *
       * Asked for on 2026-09-12, after playing. Scarcity that the player
       * cannot see the shape of is not scarcity, it is noise.
       */
      drops: [
        { item: 53 /* PineLog */, min: 1, max: 1 },
        { item: 250 /* Resin */, min: 1, max: 1 },
      ],
    }),
  def(BlockId.PineLeaves, "pine_leaves", "соснова глиця", "pine needles",
    { top: 115, bottom: 115, side: 115 },
    {
      opaque: false,
      drops: [{ item: 56 /* PineSapling */, min: 1, max: 1, chance: 0.085 }],
    }),
  def(BlockId.PineSapling, "pine_sapling", "сосновий паросток", "pine sapling",
    { top: 117, bottom: 117, side: 117 },
    { solid: false, opaque: false, render: "cross" }),
  // One turnable stone per building, all cut from the same table so a third
  // building is a row there rather than another copy of this.
  ...PLAN_STONES.flatMap((stone) =>
    ([0, 1, 2, 3] as DoorFacing[]).map((f): BlockDef => ({
      id: stone.base + f,
      name: `${stone.building}_stone_${f}`,
      label: stone.label,
      en: stone.labelEn,
      solid: true,
      opaque: true,
      translucent: false,
      render: "cube",
      facing: f,
      drops: [{ item: stone.base, min: 1, max: 1 }],
      faces: {
        top: stone.tiles[1], bottom: stone.tiles[1],
        side: stone.tiles[0], front: stone.tiles[0],
      },
    }))),
  def(BlockId.WhiteStone, "white_stone", "білий камінь", "white stone",
    { top: 124, bottom: 124, side: 124 }),
  def(BlockId.GreenTile, "green_tile", "зелена черепиця", "green roof tile",
    { top: 125, bottom: 125, side: 125 }),
  def(BlockId.Gilt, "gilt", "позолочена бляха", "gilded sheet",
    { top: 126, bottom: 126, side: 126 }),
  def(BlockId.RubyStar, "ruby_star", "рубінова зірка", "ruby star",
    { top: 127, bottom: 127, side: 127 }),
  // Porcelain: you see round it, so it must not cull the block behind.
  def(BlockId.Toilet, "toilet", "унітаз", "toilet",
    { top: 138, bottom: 137, side: 137 }, { opaque: false }),
  /*
   * The same picture on all six faces, on purpose.
   *
   * A block with a marked top would let you read the hill from above, and
   * reading the hill is the one thing this map must not allow. It drops
   * nothing: what it is worth is the hole it leaves.
   */
  /*
   * Сходи, in oak and in stone. Four facings each.
   *
   * The same tiles the whole block wears - a stair is that material cut to a
   * shape, not a different substance, and giving it its own texture would be
   * two things to keep in step for no gain.
   */
  /*
   * And the same eight again, turned over.
   *
   * A stair put up against the underside of a block hangs from it: the slab
   * is at the top of the cell and the step under it. Their own ids because
   * the two ranges they would otherwise have to grow into are four apart -
   * see `FLIPPED_STAIRS_BASE` - and they drop the ordinary stair, so
   * breaking one hands back something you can put either way up.
   */
  ...([0, 1, 2, 3] as DoorFacing[]).flatMap((f): BlockDef[] =>
    [false, true].map((stone): BlockDef => ({
      id: flippedStairsId(stone, f),
      name: `${stone ? "stone" : "wood"}_stairs_flipped_${f}`,
      label: stone ? "кам'яні сходи" : "дерев'яні сходи",
      en: stone ? "stone stairs" : "wooden stairs",
      solid: true,
      opaque: false,
      translucent: false,
      render: "stairs",
      facing: f,
      drops: [{
        item: stone ? stoneStairsId(0) : woodStairsId(0), min: 1, max: 1,
      }],
      faces: stone
        ? { top: 4, bottom: 4, side: 4 }
        : { top: 20, bottom: 20, side: 20 },
    }))),
  ...([0, 1, 2, 3] as DoorFacing[]).map((f): BlockDef => ({
    id: woodStairsId(f),
    name: `wood_stairs_${f}`,
    label: "дерев'яні сходи",
    en: "wooden stairs",
    solid: true,
    // Not opaque: half the cell is air, so whatever is behind it must still
    // be drawn or a staircase punches holes in the wall it runs up.
    opaque: false,
    translucent: false,
    render: "stairs",
    facing: f,
    // Always the one facing, so a broken stair is a stair to place again
    // rather than eight different items on the belt.
    drops: [{ item: woodStairsId(0), min: 1, max: 1 }],
    faces: { top: 20, bottom: 20, side: 20 },
    icon: 201,
  })),
  ...([0, 1, 2, 3] as DoorFacing[]).map((f): BlockDef => ({
    id: stoneStairsId(f),
    name: `stone_stairs_${f}`,
    label: "кам'яні сходи",
    en: "stone stairs",
    solid: true,
    opaque: false,
    translucent: false,
    render: "stairs",
    facing: f,
    drops: [{ item: stoneStairsId(0), min: 1, max: 1 }],
    faces: { top: 4, bottom: 4, side: 4 },
    icon: 200,
  })),
  /*
   * Тин, and the хвіртка through it.
   *
   * Both in oak, both drawn with shapes this game already has: the fence is
   * the мазанка wall's post-and-arms in wood, and the gate is a шутер that
   * happens to stand in a fence. Neither is opaque - they fill a fraction of
   * their cell, and culling what is behind one would open a hole through the
   * fence you are looking at.
   */
  {
    id: LAVA as BlockId, name: "lava", label: "лава", en: "lava",
    // Walked into rather than stood on, see-through enough to glow through
    // its own surface, and it leaves nothing in your hands.
    solid: false, opaque: false, translucent: true,
    render: "cube",
    faces: { top: 155, bottom: 155, side: 155 },
    drops: [],
  },
  {
    /*
     * A pool of нафта. Like water to a body in it and unlike water to look
     * at: it is nearly black, and what it is for is the тank.
     */
    id: OIL as BlockId, name: "oil", label: "нафта", en: "crude oil",
    solid: false, opaque: false, translucent: true,
    render: "cube",
    faces: { top: 194, bottom: 194, side: 194 },
    drops: [],
  },
  {
    id: FENCE as BlockId, name: "fence", label: "тин", en: "fence",
    solid: true, opaque: false, translucent: false,
    render: "wall",
    // Woven willow rather than oak planks, which is what it was wearing and
    // which made it read as a plank wall half a block thick.
    faces: { top: 162, bottom: 162, side: 161 },
  },
  ...([0, 1, 2, 3] as DoorFacing[]).flatMap((f): BlockDef[] =>
    [false, true].map((open): BlockDef => ({
      id: gateId(f, open) as BlockId,
      name: `gate_${f}_${open ? "open" : "shut"}`,
      label: "хвіртка",
      en: "gate",
      // Open, you walk through it; shut, you do not. The same as a door.
      solid: !open,
      opaque: false,
      translucent: false,
      render: "door",
      facing: f,
      // Always the one item, so a gate taken down is a gate to put up again
      // rather than eight things on the belt.
      drops: [{ item: gateId(0, false), min: 1, max: 1 }],
      // The same weave as the тин it stands in: a gate of planks in a woven
      // fence reads as a hole with a door in it.
      faces: { top: 162, bottom: 162, side: 161 },
    }))),
  def(BlockId.CursedStone, "cursed_stone", "проклятий камінь", "cursed stone",
    { top: 163, bottom: 163, side: 163 }),
  def(BlockId.CursedBrick, "cursed_brick", "проклята цегла", "cursed brick",
    { top: 164, bottom: 164, side: 164 }),
  /*
   * The lamp is a full block and it is *not* opaque.
   *
   * A light source that culls its neighbours' faces is a light source with a
   * black hole around it - the same rule the смолоскип and the зірка follow.
   */
  def(BlockId.CursedLamp, "cursed_lamp", "синій ліхтар", "blue lantern",
    { top: 165, bottom: 165, side: 165 }, { opaque: false }),
  /*
   * Мазанка, which is what a хата is actually made of.
   *
   * Clay daubed over a woven frame and limewashed white every spring. It is
   * the one material the country is unmistakable by: a village of plank
   * boxes could be anywhere, and a row of white walls under straw is a
   * Ukrainian village and nothing else.
   */
  /*
   * The whitewashed wall, which is not «мазанка».
   *
   * Мазанка is the daub at id 9 - brown, and what a wall is *made* of. This
   * is that wall limed, and both were called «мазанка», which put two
   * different blocks with two different textures under one name on the shelf.
   */
  def(BlockId.Whitewash, "whitewash", "побілена стіна", "whitewashed wall",
    { top: 166, bottom: 166, side: 166 }),
  def(BlockId.Snow, "snow", "сніг", "snow", { top: 149, bottom: 149, side: 149 }),
  /*
   * Ice is see-through but not translucent-blended: you can make out shapes
   * under it and the mesher still culls what is directly behind, which is
   * what keeps a frozen lake from drawing its own bed twice.
   */
  def(BlockId.Ice, "ice", "лід", "ice", { top: 150, bottom: 150, side: 150 },
    { opaque: false }),
  def(BlockId.Boom, "boom", "вибухоблок", "boom block",
    { top: 148, bottom: 148, side: 148 }, { drops: [] }),
  // The engine itself. Not opaque: it is a frame with air through it, and a
  // solid cube of timber would read as a crate.
  ...([0, 1, 2, 3] as DoorFacing[]).map((f): BlockDef => ({
    id: trebuchetId(f),
    name: `trebuchet_${f}`,
    label: "требушет",
    en: "trebuchet",
    solid: true,
    opaque: false,
    translucent: false,
    /*
     * Built out of boxes, not drawn on the side of one.
     *
     * "Так як він виглядає коли встановлений - дуже погано", and it was: a
     * требушет was a cube with a picture of a требушет painted on four of
     * its faces, which from any angle but dead-on reads as a crate. It is a
     * frame now - rails, wheels, uprights, a braced arm and the bucket at
     * the end of it - and the picture stays where a picture belongs, in the
     * hotbar.
     */
    render: "trebuchet",
    facing: f,
    drops: [{ item: TREBUCHET_BASE, min: 1, max: 1 }],
    faces: { top: 142, bottom: 141, side: 141, front: 143 },
  })),
  def(BlockId.Torch, "torch", "смолоскип", "torch",
    { top: 118, bottom: 118, side: 118 },
    { solid: false, opaque: false, render: "cross" }),
  def(BlockId.AcaciaLeaves, "acacia_leaves", "листя акації", "acacia leaves",
    { top: 19, bottom: 19, side: 19 },
    {
      opaque: false,
      drops: [{ item: 52 /* AcaciaSapling */, min: 1, max: 1, chance: 0.085 }],
    }),

  // The sprouts themselves. Cross-rendered like the crops, and like them they
  // come away in the hand - nobody needs an axe to pull up a seedling.
  def(BlockId.OakSapling, "oak_sapling", "дубовий паросток", "oak sapling",
    { top: 103, bottom: 103, side: 103 },
    { solid: false, opaque: false, render: "cross" }),
  def(BlockId.AcaciaSapling, "acacia_sapling", "паросток акації", "acacia sapling",
    { top: 104, bottom: 104, side: 104 },
    { solid: false, opaque: false, render: "cross" }),
  def(BlockId.OakPlanks, "oak_planks", "дубові дошки", "oak planks",
    { top: 20, bottom: 20, side: 20 }),
  def(BlockId.Water, "water", "вода", "water", { top: 21, bottom: 21, side: 21 },
    { solid: false, opaque: false, translucent: true, drops: [] }),
  /*
   * Дикий буряк: a ripe beet standing in the grass, and it wears exactly that
   * tile.
   *
   * Sharing the sown crop's ripe picture rather than getting one of its own,
   * because that is what it *is* - the sown one is this plant in a row. Wild
   * wheat has its own tile because the sown kind changes colour as it ripens
   * and the wild kind never does; a beet is a beet.
   */
  def(BlockId.WildBeet, "wild_beet", "дикий буряк", "wild beetroot",
    { top: 183, bottom: 183, side: 183 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [{ item: 294 /* BeetSeeds */, min: 1, max: 2 }],
    }),
  // You walk through it, and pulling it up gives you seeds, not the plant.
  def(BlockId.WildFlax, "wild_flax", "дикий льон", "wild flax",
    { top: 57, bottom: 57, side: 57 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [{ item: 214 /* FlaxSeeds */, min: 1, max: 2 }],
    }),
  def(BlockId.FlaxCrop0, "flax_crop_0", "паростки льону", "flax shoots",
    { top: 58, bottom: 58, side: 58 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [{ item: 214, min: 1, max: 1 }],
    }),
  def(BlockId.FlaxCrop1, "flax_crop_1", "зелений льон", "green flax",
    { top: 59, bottom: 59, side: 59 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [{ item: 214, min: 1, max: 1 }],
    }),
  def(BlockId.FlaxCrop2, "flax_crop_2", "льон у цвіту", "flax in flower",
    { top: 60, bottom: 60, side: 60 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [
        { item: 215 /* FlaxStalks */, min: 1, max: 2 },
        { item: 214 /* FlaxSeeds */, min: 1, max: 3 },
      ],
    }),
  // Coarse and unmistakable next to flax: broad palmate leaves, not stalks.
  // Adults-only, and it yields nothing yet - see ADULT_BLOCKS.
  // The ores. Each yields its own raw material rather than the block, the way
  // ore always has: what comes out of the rock is not the rock.
  // The seam, not the lump: what comes out of it is «вугілля», and one name
  // for two things is one thing nobody can pick off a shelf.
  def(BlockId.CoalOre, "coal_ore", "кам'яне вугілля", "coal seam",
    { top: 73, bottom: 73, side: 73 },
    { drops: [{ item: 225 /* Coal */, min: 1, max: 2 }] }),
  def(BlockId.IronOre, "iron_ore", "залізна руда", "iron ore", { top: 74, bottom: 74, side: 74 },
    { drops: [{ item: 226 /* IronOre */, min: 1, max: 1 }] }),
  def(BlockId.ManganeseOre, "manganese_ore", "марганцева руда", "manganese ore",
    { top: 75, bottom: 75, side: 75 },
    { drops: [{ item: 227 /* ManganeseOre */, min: 1, max: 1 }] }),
  // The seam, not the mineral: what comes out of it is «графіт», and two
  // things called the same thing is two entries nobody can tell apart.
  def(BlockId.GraphiteOre, "graphite_ore", "графітова руда", "graphite ore",
    { top: 76, bottom: 76, side: 76 },
    { drops: [{ item: 228 /* Graphite */, min: 1, max: 2 }] }),
  def(BlockId.OilShale, "oil_shale", "нафтоносний пласт", "oil shale",
    { top: 77, bottom: 77, side: 77 },
    { drops: [{ item: 229 /* CrudeOil */, min: 1, max: 1 }] }),
  /*
   * Незламний блок, and it means it.
   *
   * No drops, and nothing in the game gives it a number of blows - see
   * `blowsToBreak`, which answers nought for it before it asks about
   * creative. A block that cannot be broken but *can* be broken by the
   * person who ticked a box is not a floor, it is a suggestion.
   */
  def(BlockId.Bedrock, "bedrock", "незламний блок", "bedrock",
    { top: 188, bottom: 188, side: 188 }, { drops: [] }),
  // Адамантій comes up as itself: what you carry out of the shaft is the
  // crystal, and what you build a кайло out of is the same crystal.
  def(BlockId.Adamant, "adamant", "адамантій", "adamantium",
    { top: 189, bottom: 189, side: 189 }),
  // Шопопало. Dark, and lit from inside by something that keeps moving:
  // the white lightning over it is painted by the terrain shader, not by
  // the tile, because a still picture of a spark is a scratch.
  def(BlockId.Random, "random", "шопопало-блок", "random block",
    { top: 199, bottom: 199, side: 199 }, { drops: [] }),
  /*
   * The five прапори.
   *
   * Cross-rendered, like a смолоскип: a cube wearing a flag on all six
   * faces is a crate with a flag printed on it, and what this has to read
   * as from across a quarter of the map is cloth on a pole. No drops - a
   * flag that has been broken is a flag that has fallen, not a flag
   * somebody is carrying about.
   */
  ...Array.from({ length: FLAG_COUNT }, (_, team) => def(
    (FLAG_BASE + team) as BlockId,
    team === 4 ? "pirate_flag" : `flag_${team}`,
    team === 4 ? "чорний прапор" : "прапор",
    team === 4 ? "black flag" : "flag",
    { top: team === 4 ? 193 : 192, bottom: team === 4 ? 193 : 192,
      side: team === 4 ? 193 : 192 },
    { solid: false, opaque: false, render: "cross", drops: [] },
  )),
  // Granite is building stone, not ore: it comes up as itself.
  def(BlockId.Granite, "granite", "граніт", "granite", { top: 78, bottom: 78, side: 78 }),
  def(BlockId.Peat, "peat", "торф", "peat", { top: 79, bottom: 79, side: 79 },
    { drops: [{ item: 230 /* Peat */, min: 1, max: 1 }] }),
  def(BlockId.Ilmenite, "ilmenite", "ільменітовий пісок", "ilmenite sand",
    { top: 80, bottom: 80, side: 80 },
    { drops: [{ item: 231 /* Ilmenite */, min: 1, max: 1 }] }),
  def(BlockId.Rutile, "rutile", "рутиловий пісок", "rutile sand", { top: 81, bottom: 81, side: 81 },
    { drops: [{ item: 232 /* Rutile */, min: 1, max: 1 }] }),
  // Stone, so it wants a pick: you have to come prepared, not just come.
  def(BlockId.OrcSpawner, "orc_spawner", "оркське лігво", "orc lair",
    { top: 96, bottom: 96, side: 96 }, { drops: [] }),
  def(BlockId.WildHemp, "wild_hemp", "дикі коноплі", "wild hemp",
    { top: 66, bottom: 66, side: 66 },
    { solid: false, opaque: false, render: "cross", drops: [] }),
  def(BlockId.SpinningWheel, "spinning_wheel", "прядка", "spinning wheel",
    { top: 64, bottom: 64, side: 64 },
    { opaque: false, drops: [{ item: 218, min: 1, max: 1 }] }),
  def(BlockId.WildWheat, "wild_wheat", "дика пшениця", "wild wheat",
    { top: 22, bottom: 22, side: 22 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [{ item: 200 /* ItemId.WheatSeeds */, min: 1, max: 2 }],
    }),
  def(BlockId.PinePlanks, "pine_planks", "соснові дошки", "pine planks", { top: 116, bottom: 116, side: 116 }),
  def(BlockId.AcaciaPlanks, "acacia_planks", "дошки з акації", "acacia planks",
    { top: 36, bottom: 36, side: 36 }),

  // Glass and windows are solid to walk into but see-through, and blended
  // rather than alpha-tested - you look *into* them, not past a cut-out.
  def(BlockId.Glass, "glass", "скло", "glass", { top: 37, bottom: 37, side: 37 },
    { opaque: false, translucent: true }),
  def(BlockId.OakWindow, "oak_window", "вікно в дубовій рамі", "oak-framed window",
    { top: 38, bottom: 38, side: 38 }, { opaque: false, translucent: true }),
  def(BlockId.AcaciaWindow, "acacia_window", "вікно в рамі з акації", "acacia-framed window",
    { top: 39, bottom: 39, side: 39 }, { opaque: false, translucent: true }),

  // The vat. Empty or full, and only the full one is any use - the water is
  // what does the work, the clay only holds it.
  def(BlockId.Vat, "vat", "глиняний чан", "clay vat", { top: 53, bottom: 52, side: 52 },
    { opaque: false, drops: [{ item: 213, min: 1, max: 1 }] }),
  def(BlockId.VatFull, "vat_full", "чан із водою", "vat of water",
    { top: 54, bottom: 52, side: 52 },
    { opaque: false, drops: [{ item: 213, min: 1, max: 1 }] }),

  // Ploughed earth is still earth: break it and you get the dirt back.
  def(BlockId.Farmland, "farmland", "рілля", "ploughed ground", { top: 24, bottom: 2, side: 2 },
    { drops: [{ item: 2 /* BlockId.Dirt */, min: 1, max: 1 }] }),
  def(BlockId.FarmlandWet, "farmland_wet", "рілля (волога)", "ploughed ground (wet)",
    { top: 25, bottom: 2, side: 2 },
    { drops: [{ item: 2 /* BlockId.Dirt */, min: 1, max: 1 }] }),

  // Sown wheat. Pulling it up early costs you the crop but not the seed.
  def(BlockId.WheatCrop0, "wheat_crop_0", "паростки пшениці", "wheat shoots",
    { top: 28, bottom: 28, side: 28 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [{ item: 200 /* WheatSeeds */, min: 1, max: 1 }],
    }),
  def(BlockId.WheatCrop1, "wheat_crop_1", "зелена пшениця", "green wheat",
    { top: 29, bottom: 29, side: 29 },
    {
      solid: false, opaque: false, render: "cross",
      drops: [{ item: 200 /* WheatSeeds */, min: 1, max: 1 }],
    }),
  // Ripe: grain to eat, straw for the roof, and seed for next year.
  def(BlockId.WheatCrop2, "wheat_crop_2", "стигла пшениця", "ripe wheat",
    { top: 30, bottom: 30, side: 30 },
    {
      solid: false, opaque: false, render: "cross",
      // Seed and grain are the same thing - you either sow it or eat it - so
      // there is one item, and the harvest has to be worth more than the one
      // seed it cost to sow.
      drops: [
        { item: 200 /* WheatSeeds */, min: 2, max: 4 },
        { item: 7 /* BlockId.Thatch */, min: 1, max: 1, chance: 0.5 },
      ],
    }),
];

/**
 * Door blocks, generated rather than written out - thirty-two hand-written
 * entries would be thirty-two chances to transpose a digit.
 *
 * A closed door is solid: the collision grid works in whole cells, so a thin
 * panel cannot be represented. An open door is passable, which is what the
 * player actually cares about. The visible panel is thin either way.
 */
const DOOR_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  const wood = ["oak", "acacia"] as const;
  const label = ["дубові двері", "двері з акації"] as const;
  const labelEn = ["oak door", "acacia door"] as const;
  // Lower and upper halves of each wood have their own tile.
  const tiles = [[32, 33], [34, 35]] as const;

  for (let w = 0; w < 2; w++) {
    for (let f = 0; f < 4; f++) {
      for (const upper of [false, true]) {
        for (const open of [false, true]) {
          const id = doorId(w as DoorWood, f as DoorFacing, upper, open);
          const tile = tiles[w]![upper ? 1 : 0];
          out.push({
            id,
            name: `${wood[w]}_door_${f}_${upper ? "upper" : "lower"}_${open ? "open" : "shut"}`,
            label: label[w]!,
            en: labelEn[w]!,
            solid: !open,
            opaque: false,
            translucent: false,
            render: "door",
            // Breaking either half yields one door item, not two.
            drops: [{ item: 204 + w, min: 1, max: 1 }],
            faces: { top: tile, bottom: tile, side: tile },
          });
        }
      }
    }
  }
  return out;
})();

for (const d of DOOR_BLOCKS) (BLOCKS as BlockDef[]).push(d);

/** Both finishes, each turned four ways. Generated, like the doors. */
const STOVE_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  const names = ["stove", "stove_limed"] as const;
  const labels = ["піч", "побілена піч"] as const;
  const labelsEn = ["stove", "limewashed stove"] as const;
  // side, front, top per finish, and the front again with a fire in it.
  const tiles = [[40, 41, 42, 120], [43, 44, 45, 121]] as const;

  for (let lit = 0; lit < 2; lit++) {
  for (let finish = 0; finish < 2; finish++) {
    for (let f = 0; f < 4; f++) {
      const [side, coldFront, top, litFront] = tiles[finish]!;
      const front = lit === 1 ? litFront : coldFront;
      out.push({
        id: stoveId(finish as StoveFinish, f as DoorFacing, lit === 1),
        name: `${names[finish]}_${f}${lit === 1 ? "_lit" : ""}`,
        label: lit === 1 ? `${labels[finish]!} (палає)` : labels[finish]!,
        en: lit === 1 ? `${labelsEn[finish]!} (alight)` : labelsEn[finish]!,
        solid: true,
        opaque: true,
        translucent: false,
        render: "cube",
        facing: f as DoorFacing,
        // Whichever way it is turned, breaking it gives back one stove.
        drops: [{ item: 206 + finish, min: 1, max: 1 }],
        faces: { top, bottom: side, side, front },
      });
    }
  }
  }
  return out;
})();

for (const s of STOVE_BLOCKS) (BLOCKS as BlockDef[]).push(s);

/** Two woods, four facings, open or shut. Generated, like the doors. */
const SHUTTER_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  const wood = ["oak", "acacia"] as const;
  const label = ["дубові віконниці", "віконниці з акації"] as const;
  const labelEn = ["oak shutters", "acacia shutters"] as const;
  const tiles = [46, 47] as const;

  for (let w = 0; w < 2; w++) {
    for (let f = 0; f < 4; f++) {
      for (const open of [false, true]) {
        const tile = tiles[w]!;
        out.push({
          id: shutterId(w as DoorWood, f as DoorFacing, open),
          name: `${wood[w]}_shutter_${f}_${open ? "open" : "shut"}`,
          label: label[w]!,
          en: labelEn[w]!,
          // Shut, it fills the cell; open, it is flat against the wall and out
          // of the way. Same compromise as the doors, for the same reason.
          solid: !open,
          opaque: false,
          translucent: false,
          render: "door",
          drops: [{ item: 208 + w, min: 1, max: 1 }],
          faces: { top: tile, bottom: tile, side: tile },
        });
      }
    }
  }
  return out;
})();

for (const sh of SHUTTER_BLOCKS) (BLOCKS as BlockDef[]).push(sh);

/**
 * Bed halves, four facings each.
 *
 * `facing` points from the foot toward the head, so the head's board faces
 * that way and the foot's faces back down the bed.
 */
const BED_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (const head of [false, true]) {
    for (let f = 0; f < 4; f++) {
      out.push({
        id: bedId(head, f as DoorFacing),
        name: `bed_${head ? "head" : "foot"}_${f}`,
        label: "ліжко",
        en: "bed",
        // Low enough to step onto, and you never walk through it.
        solid: true,
        opaque: false,
        translucent: false,
        render: "bed",
        facing: (head ? f : (f + 2) % 4) as DoorFacing,
        // Both halves give back one bed, not two.
        drops: [{ item: 210, min: 1, max: 1 }],
        faces: {
          top: head ? 48 : 49,
          bottom: 51,
          side: 50,
          front: 51,
        },
      });
    }
  }
  return out;
})();

for (const b of BED_BLOCKS) (BLOCKS as BlockDef[]).push(b);

/** A rushnyk on each of the four walls. */
const RUSHNYK_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (let f = 0; f < 4; f++) {
    out.push({
      id: rushnykId(f as DoorFacing),
      name: `rushnyk_${f}`,
      label: "рушник",
      en: "rushnyk",
      // Cloth on a wall: you walk straight through it.
      solid: false,
      opaque: false,
      translucent: false,
      render: "door",
      drops: [{ item: 219, min: 1, max: 1 }],
      faces: { top: 65, bottom: 65, side: 65 },
    });
  }
  return out;
})();

for (const r of RUSHNYK_BLOCKS) (BLOCKS as BlockDef[]).push(r);

/**
 * The same смолоскип, hung on each of the four walls.
 *
 * It drops the torch item and nothing else, so taking one off a wall and
 * putting it on the floor is the same torch - the facing is where it *is*,
 * not what it is.
 */
const WALL_TORCH_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (let f = 0; f < 4; f++) {
    out.push({
      id: wallTorchId(f as DoorFacing),
      name: `wall_torch_${f}`,
      label: "смолоскип",
      en: "torch",
      solid: false,
      opaque: false,
      translucent: false,
      render: "torch",
      drops: [{ item: BlockId.Torch, min: 1, max: 1 }],
      faces: { top: 118, bottom: 118, side: 118 },
    });
  }
  return out;
})();

for (const t of WALL_TORCH_BLOCKS) (BLOCKS as BlockDef[]).push(t);

/** A скриня turned four ways. Painted front, plain sides, banded lid. */
const CHEST_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (let f = 0; f < 4; f++) {
    out.push({
      id: chestId(f as DoorFacing),
      name: `chest_${f}`,
      label: "скриня",
      en: "chest",
      solid: true,
      opaque: false,
      translucent: false,
      // Drawn by a model the scene stands in the cell - "заміни текстуру
      // скрині на об'єкт" - so not opaque: what is behind it shows round it.
      render: "model",
      facing: f as DoorFacing,
      // However it is turned, it comes back as one chest. What was inside is
      // not a drop - it goes straight to the bag, see main.ts.
      drops: [{ item: 233 /* Chest */, min: 1, max: 1 }],
      faces: { top: 92, bottom: 91, side: 91, front: 90 },
    });
  }
  return out;
})();

for (const c of CHEST_BLOCKS) (BLOCKS as BlockDef[]).push(c);

/**
 * The мазанка wall.
 *
 * Four identical defs rather than one, so that a world saved while these ids
 * meant four facings still opens. Only WALL_BASE is ever placed now.
 */
const WALL_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (let f = 0; f < WALL_COUNT; f++) {
    out.push({
      id: WALL_BASE + f,
      name: f === 0 ? "mazanka_wall" : `mazanka_wall_old_${f}`,
      label: "мазана стіна",
      en: "daub wall",
      // Solid, and it collides as a whole cube - the same approximation a
      // closed door has always had. In Minecraft a wall is a block and a half
      // tall so you cannot jump it; that needs shaped collision, which is its
      // own piece of work and would fix the door and the bed with it.
      solid: true,
      // Never opaque: it fills a fraction of its cell, so culling the
      // neighbour behind it would open a hole straight through the wall.
      opaque: false,
      translucent: false,
      render: "wall",
      drops: [{ item: 236 /* MazankaWall */, min: 1, max: 1 }],
      faces: { top: 95, bottom: 95, side: 95 },
    });
  }
  return out;
})();

for (const w of WALL_BLOCKS) (BLOCKS as BlockDef[]).push(w);

/**
 * Колоди на боці.
 *
 * "Зроби щоб колоди можливо було ставити горизонтально." A trunk is the one
 * thing in this game that has a direction built into it - end grain at the
 * cut ends, bark down the sides - and until now it only ever stood up. Laid
 * across a gap it is a beam; stacked, it is a зруб wall; and neither could be
 * built.
 *
 * **At 300 on purpose.** `FIRST_NON_BLOCK_ITEM` is 200, so nothing numbered
 * here can ever become an item - which is exactly right: a lying log is a
 * standing log put down a different way, not a second thing to carry.
 * Breaking one hands back the upright, and the bag never learns that any of
 * this exists.
 *
 * Above 200 is not enough on its own: a block id and a plain item id share
 * one number space, because a save is a list of bare numbers. 200 to 293 is
 * the plain items and 256 upwards is flowing water, so this starts past all
 * of them.
 *
 * Two per wood rather than three: a log standing up is the block that was
 * already there, so only X and Z are new.
 */
export const LOG_AXIS_BASE = 300;

/** The standing logs, in the order their lying versions are numbered. */
export const UPRIGHT_LOGS: readonly number[] = [
  BlockId.OakLog, BlockId.AcaciaLog, BlockId.PineLog,
];

/** Bark drawn lying down, one per wood, in the same order. */
const LYING_BARK: readonly number[] = [178, 179, 180];

/** The lying form of a standing log, along X (`axis` 0) or Z (`axis` 2). */
export function lyingLogId(upright: number, axis: 0 | 2): number {
  const wood = UPRIGHT_LOGS.indexOf(upright);
  if (wood < 0) return upright;
  return LOG_AXIS_BASE + wood * 2 + (axis === 0 ? 0 : 1);
}

export function isLyingLog(id: number): boolean {
  return id >= LOG_AXIS_BASE && id < LOG_AXIS_BASE + UPRIGHT_LOGS.length * 2;
}

/**
 * Which way a log runs: 0 along X, 1 standing up, 2 along Z.
 *
 * The same numbering the mesher uses for its axes, so `faceTile` can compare
 * the two without a translation table in between.
 */
export function logAxis(id: number): 0 | 1 | 2 {
  if (!isLyingLog(id)) return 1;
  return (id - LOG_AXIS_BASE) % 2 === 0 ? 0 : 2;
}

/** What a lying log is made of, which is what breaking one gives back. */
export function uprightLog(id: number): number {
  if (!isLyingLog(id)) return id;
  return UPRIGHT_LOGS[Math.floor((id - LOG_AXIS_BASE) / 2)] ?? id;
}

/**
 * Which log to put down, given the face that was clicked.
 *
 * The face settles it, the way it settles which way up a stair goes: click
 * the top or the bottom of something and the log stands up; click a side and
 * it lies along the axis of that side. Here rather than in the placement
 * code because it is a rule and not a gesture - a branch in main.ts cannot
 * be argued with in a test, and this can.
 */
export function logForFace(
  upright: number, nx: number, ny: number, nz: number,
): number {
  if (ny !== 0) return upright;
  if (nx !== 0) return lyingLogId(upright, 0);
  if (nz !== 0) return lyingLogId(upright, 2);
  return upright;
}

const LYING_LOG_BLOCKS: BlockDef[] = UPRIGHT_LOGS.flatMap(
  (upright, wood): BlockDef[] => ([0, 2] as const).map((axis): BlockDef => {
    // Read out of BLOCKS rather than through `blockDef`: the lookup table
    // that backs it is built at the bottom of this file, and this runs while
    // the list it is built from is still being written.
    const standing = BLOCKS.find((b) => b.id === upright)!;
    return {
      id: lyingLogId(upright, axis),
      /*
       * `oak_x_log`, not `oak_log_x`.
       *
       * Every rule in this file that has to know what a log *is* - what it
       * burns like, whether a кремль may flatten it - reads the end of the
       * name. A name ending in the axis would quietly stop being a log for
       * all of them at once.
       */
      name: `${standing.name.replace(/_log$/, "")}_${axis === 0 ? "x" : "z"}_log`,
      label: standing.label,
      en: standing.en,
      solid: true,
      opaque: true,
      translucent: false,
      render: "cube",
      drops: [{ item: upright, min: 1, max: 1 }],
      faces: {
        top: standing.faces.top,
        bottom: standing.faces.bottom,
        side: LYING_BARK[wood] ?? standing.faces.side,
      },
    };
  }),
);

for (const log of LYING_LOG_BLOCKS) (BLOCKS as BlockDef[]).push(log);

/**
 * Буряк, у землі.
 *
 * "Додай буряк і нехай в них росте теж." Three stages like everything else
 * that is sown, and numbered past `FIRST_NON_BLOCK_ITEM` on purpose: пшениця's
 * stages sit at 22-24 and each of them quietly became an item nobody can hold
 * and nobody should see. A crop block is a state of the ground, not a thing to
 * carry - the seed is the thing to carry - so these live where no item can
 * reach them.
 */
export const BEET_CROP_BASE = 306;

export function beetCropId(stage: 0 | 1 | 2): number {
  return BEET_CROP_BASE + stage;
}

const BEET_BLOCKS: BlockDef[] = ([0, 1, 2] as const).map((stage): BlockDef => ({
  id: beetCropId(stage),
  name: `beet_crop_${stage}`,
  label: stage === 2 ? "стиглий буряк" : stage === 1 ? "буряк" : "паростки буряка",
  en: stage === 2 ? "ripe beetroot" : stage === 1 ? "beetroot" : "beetroot shoots",
  solid: false,
  opaque: false,
  translucent: false,
  render: "cross",
  faces: { top: 181 + stage, bottom: 181 + stage, side: 181 + stage },
  // Pulled early it is seed back and nothing else; ripe it is the root, and
  // seed enough that a row of it is worth sowing.
  drops: stage === 2
    ? [
      { item: 295 /* PlainItemId.Beet */, min: 1, max: 1 },
      { item: 294 /* PlainItemId.BeetSeeds */, min: 1, max: 3 },
    ]
    : [{ item: 294 /* PlainItemId.BeetSeeds */, min: 1, max: 1 }],
}));

for (const b of BEET_BLOCKS) (BLOCKS as BlockDef[]).push(b);

/**
 * The same wall in granite.
 *
 * "Те саме й для граніта - стіни." The мазанка one is a daubed field wall;
 * this is the dressed stone a gate or a churchyard was built of, and the
 * only difference in the code is which tile it wears.
 */
const GRANITE_WALL_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (let f = 0; f < GRANITE_WALL_COUNT; f++) {
    out.push({
      id: GRANITE_WALL_BASE + f,
      name: f === 0 ? "granite_wall" : `granite_wall_old_${f}`,
      label: "гранітна стіна",
      en: "granite wall",
      solid: true,
      opaque: false,
      translucent: false,
      render: "wall",
      drops: [{ item: 290 /* GraniteWall */, min: 1, max: 1 }],
      faces: { top: 174, bottom: 174, side: 174 },
    });
  }
  return out;
})();

for (const w of GRANITE_WALL_BLOCKS) (BLOCKS as BlockDef[]).push(w);

/**
 * The two плити, which are the two wall materials lying down.
 *
 * Their faces are the parent block's own tiles: half a block of мазанка is
 * мазанка, and giving it a tile of its own would be painting the same thing
 * twice. The *item* has an icon of its own, because a square of daub in the
 * hotbar says nothing about what it puts down.
 */
for (const [id, name, label, en, tile, drop] of [
  [MAZANKA_SLAB, "mazanka_slab", "мазана плита", "daub slab", 10, 291],
  [GRANITE_SLAB, "granite_slab", "гранітна плита", "granite slab", 78, 292],
] as const) {
  (BLOCKS as BlockDef[]).push({
    id: id as BlockId,
    name, label, en,
    solid: true,
    // Never opaque: it fills half its cell, so culling the neighbour behind
    // it would open a hole straight through the floor.
    opaque: false,
    translucent: false,
    render: "slab",
    drops: [{ item: drop, min: 1, max: 1 }],
    faces: { top: tile, bottom: tile, side: tile },
  });
}

/**
 * The eight flowing levels.
 *
 * Drawn as full cubes with the source's own tile. Minecraft slopes its flowing
 * water, which looks better and costs a great deal: a partial-height fluid is
 * excluded from greedy merging, needs its own face-culling rule against the
 * full-height source beside it, and has to be got right in two meshers. Full
 * cubes give the behaviour asked for - water that spreads eight blocks and
 * stops - with no rendering risk at all. The slope is a separate job.
 */
const WATER_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (let level = 1; level <= WATER_RANGE; level++) {
    out.push({
      id: waterFlowId(level),
      name: `water_flow_${level}`,
      label: "вода",
      en: "water",
      solid: false,
      opaque: false,
      translucent: true,
      // Not a cube: it stands lower the further it has run.
      render: "fluid",
      drops: [],
      // Its own tile, so the shader can tell moving water from still water and
      // scroll one without scrolling the whole sea.
      faces: { top: 67, bottom: 67, side: 67 },
    });
  }
  return out;
})();

for (const w of WATER_BLOCKS) (BLOCKS as BlockDef[]).push(w);

/**
 * The four flowing levels of лава.
 *
 * Full cubes drawn with the pool's own tile, the same compromise the water
 * flows make and for the same reason. They light themselves like the pool
 * does - a stream of лава running down a cave wall that went dark as it left
 * the pool would be the one thing in this game that gives off less light the
 * closer it gets to you.
 */
const LAVA_BLOCKS: BlockDef[] = (() => {
  const out: BlockDef[] = [];
  for (let level = 1; level <= OIL_RANGE; level++) {
    out.push({
      id: oilFlowId(level) as BlockId,
      name: `oil_flow_${level}`,
      label: "нафта",
      en: "crude oil",
      solid: false,
      opaque: false,
      translucent: true,
      render: "fluid",
      drops: [],
      faces: { top: 194, bottom: 194, side: 194 },
    });
  }
  for (let level = 1; level <= LAVA_RANGE; level++) {
    out.push({
      id: lavaFlowId(level) as BlockId,
      name: `lava_flow_${level}`,
      label: "лава",
      en: "lava",
      solid: false,
      opaque: false,
      translucent: true,
      render: "fluid",
      drops: [],
      faces: { top: 155, bottom: 155, side: 155 },
    });
  }
  return out;
})();

for (const l of LAVA_BLOCKS) (BLOCKS as BlockDef[]).push(l);

/** Fast lookups, built once. Both are hot paths - collision and meshing. */
/**
 * Sized past the highest id - ids are Uint16, lookups are cheap. The water
 * range at 256 is the current ceiling; this must stay above it.
 */
const LOOKUP_SIZE = 1024;

const SOLID: Uint8Array = (() => {
  const a = new Uint8Array(LOOKUP_SIZE);
  for (const b of BLOCKS) a[b.id] = b.solid ? 1 : 0;
  return a;
})();

const OPAQUE: Uint8Array = (() => {
  const a = new Uint8Array(LOOKUP_SIZE);
  for (const b of BLOCKS) a[b.id] = b.opaque ? 1 : 0;
  return a;
})();

const TRANSLUCENT: Uint8Array = (() => {
  const a = new Uint8Array(LOOKUP_SIZE);
  for (const b of BLOCKS) a[b.id] = b.translucent ? 1 : 0;
  return a;
})();

/** Does the player collide with this block? */
export function isSolid(id: number): boolean {
  return SOLID[id] === 1;
}

/** Does this block hide the face of whatever is behind it? */
export function isOpaque(id: number): boolean {
  return OPAQUE[id] === 1;
}

/** The crown of any of the three trees. What makes a wood a wood. */
export function isLeaf(id: number): boolean {
  return id === BlockId.OakLeaves || id === BlockId.AcaciaLeaves ||
    id === BlockId.PineLeaves;
}

/** A паросток of either species, waiting to become a tree. */
export function isSapling(id: number): boolean {
  return id === BlockId.OakSapling || id === BlockId.AcaciaSapling ||
    id === BlockId.PineSapling;
}

/** Can the hoe break this into farmland? */
export function isTillable(id: number): boolean {
  return id === BlockId.Grass || id === BlockId.Dirt || id === BlockId.Chornozem;
}

/**
 * Every sown crop, in the order it grows.
 *
 * A table rather than a chain of ifs: two crops is where hand-written
 * successors start being a place to make a typo, and a third would settle it.
 */
const CROP_CHAINS: readonly (readonly number[])[] = [
  [BlockId.WheatCrop0, BlockId.WheatCrop1, BlockId.WheatCrop2],
  [BlockId.FlaxCrop0, BlockId.FlaxCrop1, BlockId.FlaxCrop2],
  // The third one, which is what the comment above said would settle it.
  [beetCropId(0), beetCropId(1), beetCropId(2)],
];

const NEXT_STAGE = new Map<number, number>();
const ALL_CROPS = new Set<number>();
for (const chain of CROP_CHAINS) {
  chain.forEach((id, i) => {
    ALL_CROPS.add(id);
    const next = chain[i + 1];
    if (next !== undefined) NEXT_STAGE.set(id, next);
  });
}

/** Any sown crop, at any stage. */
export function isCrop(id: number): boolean {
  return ALL_CROPS.has(id);
}

/** The next stage of growth, or 0 when it is already ripe. */
export function nextCropStage(id: number): number {
  return NEXT_STAGE.get(id) ?? 0;
}

/**
 * Does this block fall down when what it stands on is removed?
 *
 * Crops and wild wheat both do. Without it, digging a field out from under a
 * crop leaves it hanging in the air.
 */
export function needsSupport(id: number): boolean {
  return isCross(id);
}

/** A retting vat, with or without water in it. */
export function isVat(id: number): boolean {
  return id === BlockId.Vat || id === BlockId.VatFull;
}

/** Ploughed soil, wet or dry. */
export function isFarmland(id: number): boolean {
  return id === BlockId.Farmland || id === BlockId.FarmlandWet;
}

/** Is this block built from crossed quads rather than a cube? */
export function isCross(id: number): boolean {
  return blockDef(id).render === "cross";
}

/**
 * The tile a given face of a block should use.
 *
 * Almost every block answers with top, bottom or side. A block with a `facing`
 * also has a front, and shows it on exactly one of its four walls.
 */
export function faceTile(id: number, axis: number, positive: boolean): number {
  const def = blockDef(id);
  /*
   * A колода на боці shows its cut ends along the way it lies.
   *
   * The only block whose texture depends on an axis rather than on a facing,
   * which is why it is a branch here and not a field: end grain on the two
   * faces the saw made, bark on the other four.
   */
  if (isLyingLog(id)) {
    return axis === logAxis(id) ? def.faces.top : def.faces.side;
  }
  if (axis === 1) return positive ? def.faces.top : def.faces.bottom;
  if (def.facing !== undefined && def.faces.front !== undefined) {
    // 0 north is -Z, 1 east is +X, 2 south is +Z, 3 west is -X.
    const faceDir = axis === 2 ? (positive ? 2 : 0) : (positive ? 1 : 3);
    if (faceDir === def.facing) return def.faces.front;
  }
  return def.faces.side;
}

/** Doors are neither cubes nor crosses: a thin panel against one wall. */
export function isDoorRender(id: number): boolean {
  return blockDef(id).render === "door";
}

/** A bed is a low box rather than a full cube. */
export function isBedRender(id: number): boolean {
  return blockDef(id).render === "bed";
}

/**
 * Anything the mesher draws as a post with an arm toward each neighbour.
 *
 * Asked of the *shape the block declares* and not of its id, which is what
 * this used to be. `isWall` is a range of four ids - the кремль's stone wall
 * and its three unused facings - and the тин is id 150 with `render: "wall"`
 * on it. So the тин declared itself a wall, said so in the registry, and the
 * mesher's dispatch never reached the wall branch: it fell through to the
 * box branch below and came out as a **door panel**, flat and turned
 * whichever way `panelBounds` reads out of an id that is not a door.
 *
 * That is "тин стоїть боком", and it is why straightening the ring did not
 * fix it: the geometry was right the whole time and the shape was wrong.
 */
export function isWallRender(id: number): boolean {
  return blockDef(id).render === "wall";
}

/**
 * Anything the mesher builds as a box with bounds of its own.
 *
 * Not walls. A wall is a post and some arms, which is not a box, and it was
 * in this list only because the dispatch above it happened to catch the four
 * ids anybody had tried.
 */
export function isBoxRender(id: number): boolean {
  const r = blockDef(id).render;
  return r === "door" || r === "bed";
}

/** Half a cube, lying on the floor of its cell. */
export function isSlabRender(id: number): boolean {
  return blockDef(id).render === "slab";
}

/** Drawn by a model in the scene, not by the mesher. See render/blockModels. */
export function isModelRender(id: number): boolean {
  return blockDef(id).render === "model";
}

/** A frame of timber on wheels, built out of two dozen little boxes. */
export function isTrebuchetRender(id: number): boolean {
  return blockDef(id).render === "trebuchet";
}

/** Two boxes: a slab, and half a slab on top of it. */
export function isStairsRender(id: number): boolean {
  return blockDef(id).render === "stairs";
}

/** Drawn in the world as two crossed pictures: a plant, a sapling, a flower. */
export function isCrossRender(id: number): boolean {
  return blockDef(id).render === "cross";
}

/** Drawn as a box whose height depends on how far the water has run. */
export function isFluidRender(id: number): boolean {
  return blockDef(id).render === "fluid";
}

/**
 * Anything the meshers emit one cell at a time rather than merging.
 *
 * The greedy mesher must exclude every one of these from its masks, and then
 * emit them in a second pass. Adding a render kind and forgetting one of those
 * two places produces a block that is invisible, or one that smears across its
 * neighbours - so both places ask this rather than listing kinds themselves.
 */
export function isShapeRender(id: number): boolean {
  return isCross(id) || isBoxRender(id) || isWallRender(id) ||
    isFluidRender(id) || isStairsRender(id) || isWallTorch(id) ||
    isSlabRender(id) || isTrebuchetRender(id) || isModelRender(id);
}

/**
 * Roll what breaking a block yields.
 *
 * Random, and deliberately not seeded: drops are not world state, so they need
 * not survive a reload the way terrain must.
 */
export function rollDrops(id: number): { item: number; count: number }[] {
  const def = blockDef(id);
  if (def.drops === undefined) return [{ item: id, count: 1 }];

  const out: { item: number; count: number }[] = [];
  for (const drop of def.drops) {
    if (drop.chance !== undefined && Math.random() > drop.chance) continue;
    const count = drop.min + Math.floor(Math.random() * (drop.max - drop.min + 1));
    if (count > 0) out.push({ item: drop.item, count });
  }
  return out;
}

/**
 * Can you aim at it and break it?
 *
 * Not the same as `solid`. Wild wheat is walked straight through but must
 * still be harvestable, and aiming used the collision test until that was
 * noticed - the ray passed cleanly through every plant in the world.
 *
 * Fluids stay untargetable so the ray reaches the lake bed instead of stopping
 * at the surface.
 */
/**
 * Blocks that exist only when the player has confirmed they are an adult.
 *
 * One list, so that generation, the sweep that runs when the switch goes off,
 * and the test that nothing adult is reachable in family mode all agree.
 */
const ADULT_ONLY: readonly number[] = [BlockId.WildHemp];

export function isAdultOnly(id: number): boolean {
  return ADULT_ONLY.includes(id);
}

/*
 * What the aiming ray stops at.
 *
 * Not лава. A cell of лава you can click on is a cell of лава you can pick
 * up and throw away with your bare hands, which is the whole danger gone -
 * and worse, it is the danger gone *by accident*, because the block in front
 * of the pool is what you were aiming at. The ray goes through it instead,
 * so the way to deal with a pool is what it should be: drop something into
 * it.
 */
export function isTargetable(id: number): boolean {
  return id !== BlockId.Air && !isFluid(id) && !isLava(id) && !isOil(id);
}

/**
 * Can a placed block occupy this cell?
 *
 * Air, water, and anything you can walk through - placing a block into tall
 * wheat should simply replace it rather than being refused.
 */
export function isReplaceable(id: number): boolean {
  return id === BlockId.Air || isFluid(id) || isOil(id) || !SOLID[id];
}

/** Is this block drawn in the blended pass? */
export function isTranslucent(id: number): boolean {
  return TRANSLUCENT[id] === 1;
}

/** Can the player swim in it? */
export function isFluid(id: number): boolean {
  return isWater(id);
}

/** Лава, which you can also get into, though not for long. */
export function isLava(id: number): boolean {
  return id === LAVA || isLavaFlow(id);
}

/**
 * Anything a body sinks into rather than stands on.
 *
 * Kept apart from `isFluid` on purpose. `isFluid` answers "is this water" for
 * everything in the world that cares - where a карась can live, what a свиня
 * walks round, whether a seed is underwater - and лава is none of those
 * things. This one is only about a body in it, and a body in лава behaves
 * like a body in water for exactly as long as it has left.
 */
export function isWadeable(id: number): boolean {
  return isFluid(id) || isLava(id) || isOil(id);
}

/**
 * Should the face between `id` and `neighbour` be skipped?
 *
 * Hidden by anything opaque, and also by more of the same block - otherwise
 * every leaf cluster would draw its own hollow interior, which is thousands of
 * invisible faces per tree.
 */
/**
 * Is this the world as it was found, rather than something somebody put here?
 *
 * The question a building that levels its own site has to ask before it moves
 * anything, and it is asked the safe way round: earth, rock, ore, trees and
 * water are named here, and *everything else* - every plank, every brick,
 * every door, and anything added to this game after today - counts as
 * somebody's work and stops the build.
 *
 * That default is the whole point. Getting the list wrong by omission means a
 * кремль refuses on a patch of ground it could have cleared, and the player
 * moves twenty blocks along. Getting it wrong the other way means the хата
 * somebody spent an evening on is gone with no warning and no undo.
 */
/**
 * Сніг та лід: what a fire cannot touch.
 *
 * "Вогонь запалює і горить в снігу, нехай не горить в снігу та воді." A
 * fierce bolt sets alight anything that is not untouched ground, and neither
 * snow nor ice is on that list - so a snowfield took a требушет stone and
 * smouldered for half a minute. Water was already refused by name; this is
 * the other half of the same sentence.
 */
export function isFrozen(id: number): boolean {
  return id === BlockId.Snow || id === BlockId.Ice;
}

export function isNaturalGround(id: number): boolean {
  if (id === BlockId.Air) return true;
  if (isWater(id)) return true;

  const name = blockDef(id).name;
  if (name.endsWith("_log") || name.endsWith("_leaves") ||
      name.endsWith("_sapling")) return true;
  if (name.endsWith("_ore") || name.startsWith("wild_")) return true;
  /*
   * Рілля and what grows in it are *not* natural.
   *
   * Wild wheat on the steppe is; a tilled field is somebody who walked out
   * with a hoe. Farmland was on this list for one draft, which would have let
   * a кремль flatten a wheat field without so much as a refusal - the exact
   * loss this whole rule exists to prevent, hidden inside it.
   */

  return UNTOUCHED.has(id);
}

/**
 * How tall a block is where you walk into it, in blocks.
 *
 * A block used to be solid or not, and everything solid filled its cube
 * whatever shape it was drawn in. This is the number that replaces the
 * "yes", and there are only three of them.
 *
 * A тин is a block and a half, which is Minecraft's number and is the point
 * of it: one course of fence has to be higher than a jump, or a fence is a
 * thing you step over. A плита is half. Everything else that is solid is one.
 *
 * Сходи are *not* a half. They are drawn as a slab with half a slab on top,
 * and the thing you walk into at the low end is the full cell - the shape
 * that would do them justice is two heights, and one number per block is not
 * enough for that. They keep what they have always had; a step of one block
 * carries you up them and always did.
 */
export function blockHeight(id: number): number {
  if (!isSolid(id)) return 0;
  if (isWallRender(id)) return 1.5;
  if (isSlabRender(id)) return 0.5;
  return 1;
}

/**
 * How much of one cell is solid, from its floor up: nought to one.
 *
 * Two blocks are asked about, because a тин is taller than the cell it sits
 * in and the half that sticks out has to stop somebody standing in the cell
 * above it. `below` is what is under the cell being asked about.
 */
export function cellFill(here: number, below: number): number {
  const own = blockHeight(here);
  if (own > 0) return Math.min(1, own);
  return Math.max(0, Math.min(1, blockHeight(below) - 1));
}

/** The ground itself. Everything that is not grown or dug out of it. */
const UNTOUCHED: ReadonlySet<number> = new Set<number>([
  BlockId.Grass, BlockId.Dirt, BlockId.Stone, BlockId.Chornozem,
  BlockId.Sand, BlockId.Clay, BlockId.Granite, BlockId.Peat,
  BlockId.Sandstone, BlockId.OilShale, BlockId.Ilmenite, BlockId.Rutile,
  BlockId.GraphiteOre,
]);

/**
 * How readily a block takes fire: 0 never, 1 grudgingly, 3 like straw.
 *
 * Nothing burns yet - there is no fire in this game. This is the property the
 * blocks have to *have* before a fire can read it, and it is written down
 * once, here, rather than guessed at by whatever lights the first match.
 *
 * The number that matters most is the one that is zero. Brick, dressed stone,
 * gilding and glazed tile do not burn, and no amount of wanting them to will
 * change that: a masonry fortress is not something you set alight with a
 * torch. Burning one down needs something that carries its own fire - смола
 * thrown alight - and that is a weapon, not a property of the wall.
 *
 * Worked out from the block rather than stored on it, so a new plank or a new
 * door is flammable the day it is added instead of the day somebody remembers
 * a table.
 */
export function burnRate(id: number): number {
  // Wooden things with state of their own, which have no plain name to read.
  if (isDoor(id) || isShutter(id) || isBed(id) || isChest(id)) return 2;
  // Полотно. The most flammable thing in the game, and it hangs on a wall.
  if (isRushnyk(id)) return 3;
  if (isWall(id)) return 2;

  const name = blockDef(id).name;
  if (name.endsWith("_planks") || name.endsWith("_log")) return 2;
  if (name.endsWith("_leaves") || name.endsWith("_sapling")) return 3;
  if (name.endsWith("_window")) return 1;

  return KINDLING[id] ?? 0;
}

/** What burns without being made of boards. */
const KINDLING: Readonly<Record<number, number>> = {
  [BlockId.Thatch]: 3,
  [BlockId.WildWheat]: 3,
  [BlockId.WheatCrop0]: 2, [BlockId.WheatCrop1]: 2, [BlockId.WheatCrop2]: 3,
  [BlockId.WildFlax]: 3,
  [BlockId.FlaxCrop0]: 2, [BlockId.FlaxCrop1]: 2, [BlockId.FlaxCrop2]: 3,
  [BEET_CROP_BASE]: 2, [BEET_CROP_BASE + 1]: 2, [BEET_CROP_BASE + 2]: 3,
  [BlockId.WildHemp]: 3,
  [BlockId.SpinningWheel]: 2,
  [BlockId.Torch]: 3,
  [WALL_TORCH_BASE]: 3, [WALL_TORCH_BASE + 1]: 3,
  [WALL_TORCH_BASE + 2]: 3, [WALL_TORCH_BASE + 3]: 3,
  // Fuels. Peat and oil shale are dug up to be burnt, so it would be strange
  // if they would not catch where they lie.
  [BlockId.Peat]: 2,
  [BlockId.OilShale]: 2,
  [BlockId.CoalOre]: 1,
  // Мазанка is clay and straw. Mostly clay, but the straw is in there.
  [BlockId.Mazanka]: 1,
};

export function facesHiddenBy(id: number, neighbour: number): boolean {
  // Water is not decided here. How much of its cell a water block fills
  // depends on whether there is water above it, which this cannot see - see
  // hiddenBy in the mesher, which can. Two water blocks of the same id still
  // hide each other by the rule below, which is what an ocean needs.
  return OPAQUE[neighbour] === 1 || (neighbour !== 0 && neighbour === id);
}

/** Sparse, indexed by id rather than by position in the list above. */
const BY_ID: (BlockDef | undefined)[] = (() => {
  const a: (BlockDef | undefined)[] = [];
  for (const b of BLOCKS) a[b.id] = b;
  return a;
})();

export function blockDef(id: number): BlockDef {
  return BY_ID[id] ?? BLOCKS[0]!;
}
